/**
 * English catalogue.
 *
 * A translation of the authoritative Turkish source in tr.ts. The type of this
 * object is checked against the Turkish key set, so a key added there without a
 * translation here is a compile error rather than a runtime blank.
 */
import type { tr } from './tr'

export const en: Record<keyof typeof tr, string> = {
  'app.name': 'Kimene',
  'app.tagline': 'AI workforce control plane',
  'nav.commandCenter': 'Command Center',
  'nav.skipToContent': 'Skip to content',
  'nav.organization': 'Organization',
  'nav.projects': 'Projects',
  'nav.missions': 'Missions',
  'nav.agents': 'Agents',
  'nav.liveOps': 'Live Operations',
  'nav.cockpit': 'Sessions',
  'nav.terminals': 'Terminals',
  'nav.files': 'Files',
  'nav.approvals': 'Approvals',
  'nav.pools': 'Pools',
  'nav.system': 'System',
  'nav.accounts': 'Accounts',
  'nav.models': 'Models',
  'nav.hosts': 'Hosts',
  'nav.tools': 'Tools',
  'nav.artifacts': 'Artifacts',
  'nav.analytics': 'Analytics',
  'nav.audit': 'Audit',
  'nav.profile': 'Profile',
  'nav.settings': 'Settings',
  'nav.collapse': 'Collapse menu',
  'nav.expand': 'Expand menu',
  'topbar.search': 'Search',
  'topbar.searchPlaceholder': 'Search projects, tasks, accounts or sessions',
  'topbar.live': 'Live',

  // ---- Top bar: the attention cluster ----
  'attn.calm': 'Nothing is waiting',
  'attn.waiting': '{count} sessions are waiting for you',
  'attn.quota': 'Tightest quota {pct}%',
  'attn.risks': '{count} risks',
  'attn.newBuild': 'New build — reload',
  'attn.depDown': '{name} is unreachable',
  'topbar.activeOrchestrator': 'Active orchestrator',
  'topbar.runningTasks': 'Running tasks',
  'topbar.pendingApprovals': 'Pending approvals',
  'topbar.notifications': 'Notifications',
  'topbar.userMenu': 'User menu',
  'topbar.theme': 'Theme',
  'topbar.keepAwake': 'Keep screen on',
  'topbar.keepAwakeOn': 'On',
  'topbar.keepAwakeOff': 'Off',
  'topbar.installApp': 'Install on this device',
  'topbar.installAppHint':
    'Installs Kimene as an application in its own window, without browser tabs. You can remove it at any time.',
  'topbar.fullscreen': 'Fullscreen',
  'topbar.tight': 'Tight layout',
  'topbar.tightExit': 'Normal layout',
  'topbar.tightHint': 'Closes every gap: single-line borders, nothing spent on space.',

  /* ---- The install sheet on a phone (components/InstallOffer.tsx) ------- */
  'install.title': 'Install Kimene as an app',
  'install.why': 'Opens full-screen without the browser bar, with its own icon on the home screen.',
  'install.action': 'Install',
  'install.dismiss': 'Dismiss',
  'install.iosShare': 'Share',
  'install.iosAdd': 'Add to Home Screen',
  'topbar.fullscreenExit': 'Leave fullscreen',
  'topbar.language': 'Language',
  'topbar.logout': 'Sign out',
  'topbar.account': 'Account',
  'nav.menu': 'Menu',
  'action.create': 'Create',
  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.confirm': 'Confirm',
  'action.delete': 'Delete',
  'action.edit': 'Edit',
  'action.close': 'Close',
  'action.retry': 'Try again',
  'action.refresh': 'Refresh',
  'action.viewAll': 'View all',
  'action.details': 'Details',
  'action.approve': 'Approve',
  'action.reject': 'Reject',
  'action.pause': 'Pause',
  'action.resume': 'Resume',
  'action.stop': 'Stop',
  'action.simulate': 'Simulate',
  'action.test': 'Test',
  'action.login': 'Sign in',
  'action.continue': 'Continue',
  'action.back': 'Back',
  'action.next': 'Next',
  'state.loading': 'Loading',
  'state.empty': 'Nothing here',
  'state.error': 'Something went wrong',
  'state.degraded': 'Running degraded',
  'state.unknown': 'Unknown',
  'state.never': 'Never',
  'common.state': 'State',
  'project.description': 'Description',
  'state.now': 'Now',
  'error.loadFailed': 'Could not load this data.',
  'error.loadFailedHint': 'Check the connection and try again.',
  'error.notFound': 'That record does not exist.',
  'error.forbidden': 'You do not have access to this.',
  'error.forbiddenHint':
    'Check your permissions. During setup this usually means the token was wrong, expired, or already used.',
  'error.network': 'Cannot reach the server.',
  'error.unexpected': 'An unexpected error occurred.',
  'cc.title': 'Command Center',
  'cc.subtitle': 'What the system is doing right now',
  'cc.systemHealth': 'System health',
  'cc.activeExecutor': 'Active executor',
  'cc.standbyRoute': 'Standby route',
  'cc.noStandby': 'No standby route',
  'cc.taskFlow': 'Task flow',
  'cc.running': 'Running',
  'cc.waiting': 'Waiting',
  'cc.failed': 'Failed',
  'cc.needsYou': 'Needs you',
  'cc.accountOverview': 'Account pool',
  'cc.hostResources': 'Host resources',
  'cc.hostCores': 'Cores',
  'cc.hostMemory': 'Memory',
  'cc.hostAccounts': 'Accounts',
  'cc.hostSessions': 'Sessions',
  'cc.hostStale': 'no report for {age}',
  'cc.recentRouteChanges': 'Recent route changes',
  'cc.criticalRisks': 'Critical risks',
  'cc.liveEvents': 'Live event feed',
  'cc.dependencyHealth': 'Dependency health',
  'cc.noEventsYet': 'No events yet. Start a task and they will appear here.',
  'cc.noRisks': 'No known critical risks.',
  'routing.decision': 'Routing decision',
  'routing.why': 'Why this route?',
  'routing.selected': 'Selected',
  'routing.candidates': 'Candidates considered',
  'routing.rejected': 'Rejected candidates',
  'routing.score': 'Score',
  'routing.scoreBreakdown': 'Score breakdown',
  'routing.confidence': 'Confidence',
  'routing.mode': 'Routing mode',
  'routing.modeSource': 'Mode source',
  'routing.noEligible': 'No eligible candidate',
  'routing.noEligibleHint':
    'No combination of account, model and host meets this task’s requirements. See the rejection reasons.',
  'routing.simulate': 'Route simulation',
  'routing.simulateHint': 'A simulation takes no lease and starts no session.',
  'routing.simulated': 'Simulated',
  'routing.weights': 'Weights',
  'routing.override': 'Manual selection',
  'account.title': 'Account Pool',
  'account.subtitle': 'Provider accounts and host profiles',
  'account.provider': 'Provider',
  'account.label': 'Label',
  'account.host': 'Host',
  'account.addHostHint':
    "The account's credentials live on this host, and sessions started with it run there.",
  'account.controlHostTag': 'control',
  'account.auth': 'Auth',
  'account.capacity': 'Capacity',
  'account.usage': 'Usage',
  'account.usageResets': 'resets {when}',
  'account.usageLeft': 'left',
  'account.usageWindow.session': '5-hour',
  'account.usageWindow.weekly': 'Weekly',
  'account.usageWindow.weeklyScoped': 'Weekly · model',
  'account.usageWindow.monthly': 'Monthly',
  'account.spend': 'Spend',
  'account.extraUsageOff': 'Extra usage off',
  'account.subscription': 'Subscription',
  'account.organization': 'Organization',
  'account.confidence': 'Capacity confidence',
  'account.models': 'Reachable models',
  'account.canAcceptWork': 'Can accept work',
  'account.activeSessions': 'Open sessions',
  'account.lastRateLimit': 'Last rate limit',
  'account.share': 'Make available on every host',
  'account.sharedNow': 'The credential directory is on shared storage; this account now works on every host.',
  'account.everyHost': 'every host',
  'account.lastProbe': 'Last probe',
  'account.concurrency': 'Concurrency limit',
  'account.routingWeight': 'Routing weight',
  'account.enable': 'Enable',
  'account.disable': 'Disable',
  'account.healthTest': 'Health test',
  'account.openLogin': 'Sign-in terminal',
  'account.signIn': 'Sign in',
  'account.reSignIn': 'Sign in again',
  'account.reSignInConfirm':
    'A sign-in pane opens for {label} and you complete the flow there. A successful sign-in replaces the login in this account’s credential directory — on every host, where that directory is shared. Running sessions are not interrupted.',
  'account.add': 'Add account',
  'account.addTitle': 'New account',
  'account.addProvider': 'Provider',
  'account.addLabel': 'Label',
  'account.addLabelHint': 'e.g. Team-1 · Claude',
  'account.addManaged': 'Let Kimene create its own credential directory',
  'account.addManagedHint':
    'On: a fresh, owner-only directory is created and you sign in through the cockpit. Off: you supply an existing directory path.',
  'account.addProfileDir': 'Credential directory (absolute path)',
  'account.deviceAuth': 'Sign in with a device code',
  'account.deviceAuthHint': 'Signs in by code on hosts where no browser can open (Codex only).',
  'account.addSubmit': 'Create',
  'account.addThenSignIn': 'Create and sign in',
  'account.signInStarting': 'Opening a sign-in session…',
  'account.signInPaneTitle': '{name} — sign in',
  'account.signInPaneHint':
    'The sign-in runs in the terminal below; paste the code into it. When the pane closes the sign-in is confirmed for you.',
  'account.signInOpenInTerminal': 'Open in the full terminal',
  'account.activate': 'Confirm the sign-in',
  'account.signInThenActivate':
    'The sign-in opens in a terminal and takes you there; you finish the flow in it. When the pane closes the console confirms the sign-in for you — this button is for confirming it by hand.',
  'account.activateNotReady': 'Sign-in not verified yet. Complete it in the pane and try again.',
  'account.matrix': 'Account × Model matrix',
  'account.notConfigured': 'Not configured',
  'account.notConfiguredHint': 'This slot has not been set up yet. Edit it to begin.',
  'account.capacityUnknownHint':
    'The provider reports no capacity. This value has not been measured, and is not shown as an estimate either.',
  'account.slotsTitle': 'Unconfigured slots',
  'account.reachModels': 'Models',
  'account.reachSessions': 'Sessions',
  'account.presence': 'Presence',
  'account.noProjects': 'No project run yet',
  'account.reloginHint':
    "This account's stored login is no longer valid. Sign in again so it can take work.",
  'account.delete': 'Delete',
  'account.deleteConfirm': 'Permanently delete the account {label}? This cannot be undone.',
  'account.deleteDisableFirst': 'Disable the account before deleting it.',
  'model.title': 'Model Pool',
  'model.family': 'Family',
  'model.status': 'Status',
  'model.context': 'Context',
  'model.capabilities': 'Capabilities',
  'model.lastVerified': 'Last verified',
  'model.availability': 'Availability',
  'model.verificationSource': 'Verification source',
  'project.title': 'Projects',
  'project.repositories': 'Repositories',
  'project.overview': 'Overview',
  'project.missions': 'Missions',
  'project.tasks': 'Tasks',
  'project.routing': 'Routing policy',
  'project.performance': 'Model performance',
  'project.sessions': 'Sessions',
  'project.artifacts': 'Artifacts',
  'project.configuration': 'Configuration',
  'project.decisions': 'Decisions',
  'project.audit': 'Audit',
  'project.add': 'Add project',
  'project.riskLevel': 'Risk level',
  'project.qualityGates': 'Quality gates',
  'project.configHint':
    'Kimene reads these files and never overwrites them silently. If a change is needed it shows the diff and asks.',
  'mission.title': 'Missions',
  'mission.objective': 'Objective',
  'mission.acceptance': 'Acceptance criteria',
  'mission.graph': 'Dependency graph',
  'mission.list': 'List',
  'mission.board': 'Board',
  'mission.timeline': 'Timeline',
  'mission.criticalPath': 'Critical path',
  'mission.create': 'Create mission',
  'task.state': 'State',
  'task.role': 'Role',
  'task.archetype': 'Kind',
  'task.duration': 'Duration',
  'task.progress': 'Progress',
  'task.evidence': 'Evidence',
  'task.blocker': 'Blocker',
  'task.dependencies': 'Dependencies',
  'task.attempts': 'Attempts',
  'task.openTerminal': 'Open terminal',
  'task.evidenceIncomplete': 'Evidence incomplete',
  'task.evidenceComplete': 'Evidence complete',
  'task.evidenceHint': 'A task is not complete until its evidence contract is satisfied.',
  'live.title': 'Live Operations',
  'live.sessions': 'Sessions',
  'live.terminal': 'Terminal',
  'live.messages': 'Visible messages',
  'live.timeline': 'Event timeline',
  'live.toolActivity': 'Tool activity',
  'live.changedFiles': 'Changed files',
  'live.diff': 'Diff',
  'live.tests': 'Tests',
  'live.metrics': 'Resource metrics',
  'live.start': 'Start session',
  'live.stop': 'Stop',
  'live.startTitle': 'New session',
  'live.startAccount': 'Account',
  'live.continuedAccount': 'The account this project last used',
  'live.startModel': 'Model',
  'live.startModelDefault': 'Default (let the CLI choose)',
  'live.startEffort': 'Effort',
  'live.startEffortDefault': 'Unspecified',
  'live.startProject': 'Project',
  'live.startProjectNone': 'No project (enter a path)',
  'live.startProjectPick': 'Choose a project',
  'live.startRunsOn': 'Runs on:',
  'live.startEveryHost': 'every host',
  'live.startOtherRepos': '{n} more checkout(s); this session runs in this one',
  'live.startAccountElsewhere':
    '{account} is signed in on {on}, but this session runs on {target}. Credentials are host-local: sign this account in on {target} from the Accounts screen, or pick an account that is already signed in there.',
  'live.startProjectNoRepo':
    'This project has no repository (server + directory). Add one on the project page.',
  'live.startWorkingDir': 'Working directory',
  'live.startWorkingDirHint': 'Absolute path, e.g. /srv/kimene',
  'live.startName': 'Name (optional)',
  'live.startRecord': 'Record this session',
  'live.startResume': 'Resume a previous session',
  'live.startResumeHint':
    "Opens the provider's picker of past sessions instead of a fresh conversation.",
  'live.startBypass': 'Skip approvals (dangerous mode)',
  'live.startBypassHint': 'Launches the CLI unattended, without approval prompts.',
  'live.startBypassWarn':
    "This disables the provider's approval and sandbox guards. Use it only for work you trust.",
  'live.startSubmit': 'Start',
  'live.startNoAccounts': 'No enabled account. Add one and sign in first.',
  'live.stopConfirm': 'Stop this session? Its tmux session will be terminated.',
  'live.switchAccount': 'Change the account',
  'live.switchAccountBusy':
    'The agent is working. Changing the account restarts the session, and cutting a turn in half can leave work half done — wait for it to finish or to stop for a question.',
  'live.switchTo': 'New account',
  'live.switchToHint': 'Accounts of the same provider that are signed in on {host}.',
  'live.switchPick': 'Choose an account…',
  'live.switchNoneHere':
    'No other account on {host} can continue this session. Credentials are host-local; a profile signed in on another machine cannot be used from here.',
  'live.switchWarning':
    'This session will be stopped and started again on the account you choose. Its conversation stays under the old account and the new session opens on an empty one. The project, the directory and the model stay the same.',
  'live.switchConfirm': 'Stop and restart',
  'live.handoff.failure': 'This session stopped on a confirmed failure ({reason}); the work needs to move to another account.',
  'live.handoff.recommend': 'Recommended: {account}.',
  'live.handoff.carries': 'The move carries a summary across: the new session continues where this one left off, knowing what it was doing.',
  'live.restart': 'Restart',
  'live.restartConfirmRunning':
    'Stop this session and start it again with the same settings? Work in progress is lost.',
  'live.restartConfirmEnded': 'Start this session again with the same settings?',
  'live.clear': 'Remove from list',
  'live.clearConfirm':
    'Remove this ended session from the list? Its dead tmux pane on the host is cleared away too.',
  'live.exitStatus': 'exit {code}',
  'live.exitLabel': 'Exit code',
  'live.approvals': 'Approvals',
  'live.approvalsBypassed': 'Skipped',
  'live.recording': 'Recording',
  'live.recordingOn': 'On',
  'live.sessionId': 'Session id',
  'live.effort': 'Effort',
  'live.runningFor': 'running {duration}',
  'live.ranFor': 'ran {duration}',

  // ---- Live: telemetry rail ----
  'live.rail.quotas': 'Quotas',
  'live.rail.waiting': 'Waiting for you',
  'live.rail.noWaiting': 'Nobody is waiting for an answer.',
  'live.ctx.noHost': 'This session names a host the inventory does not carry.',
  'live.ctx.noAccount': 'No account recorded',
  'live.ctx.noQuota': 'Quota not measured.',
  'live.rail.risks': 'Risks & warnings',
  'live.rail.quotaRefresh': 'Read the quotas now',
  'live.rail.quotaResets': 'resets {when}',
  'live.rail.quotaAsOf': 'read {when}',
  'live.rail.quotaNotStarted': 'This window has not opened yet',
  'live.rail.quotaNoReset': 'No reset time reported',
  'live.rail.quotaRun': '~{count} windows before {kind} resets',
  'live.rail.quotaRunMeasured': 'Worked out from the {hours}-hour window length the provider reports.',
  'live.rail.quotaRunAssumed': 'Worked out assuming a {hours}-hour window; the provider does not publish its length.',
  'live.rail.noQuotas': 'No quota published.',
  'live.rail.noRisks': 'No open risks.',
  'live.metric.cpu': 'CPU',
  'live.metric.ram': 'RAM',
  'live.metric.disk': 'Disk',
  'live.metric.gpu': 'GPU',
  'live.metric.load': 'load',
  'live.metric.cores': '{count} cores',
  'live.metric.stale': 'stale',
  'live.metric.noSample': 'no sample',
  'live.risk.host_offline': 'Host {host} is offline',
  'live.risk.node_contract_differs': 'The agent on {host} is running a different build; restarting it at a safe boundary would change what it runs.',
  'live.risk.host_stale': 'Host {host} has not reported recently',
  'live.risk.host_disk_high': 'Host {host} disk is {percent}% full',
  'live.risk.account_auth': 'Account {account} needs a login',
  'live.risk.account_exhausted': 'Account {account} hit a rate limit',
  'live.risk.no_eligible_account': 'No account can accept work',
  'live.risk.dependency_down': '{dependency} is unreachable',
  'live.risk.unlisted_sessions': '{count} session(s) on the host that cannot be listed: {sessions}',
  'live.risk.unlisted_dead': '{count} finished session(s) left on the host, cleared away automatically: {sessions}',
  'live.rail.clearUnlisted': 'Clear away now',
  'live.rail.clearUnlistedHint': 'Remove the finished panes from the host without waiting. A running session is never touched.',
  'live.rail.clearUnlistedFailed': 'Could not clear them away. The server log says why.',
  'live.resizeSessions': 'Resize the sessions column',
  'live.resizeRail': 'Resize the telemetry column',
  'live.hideSessions': 'Hide the sessions panel',
  'live.showSessions': 'Show the sessions panel',
  'live.hideRail': 'Hide the telemetry panel',
  'live.showRail': 'Show the telemetry panel',
  'live.open': 'Open in terminal',
  'live.copyId': 'Copy id',
  'terminal.control': 'Take control',
  'terminal.attach': 'Attach',
  'terminal.detach': 'Detach',
  'terminal.close': 'Close terminal',
  'terminal.reconnect': 'Reconnect',
  'terminal.reconnecting': 'Reconnecting',
  'terminal.disconnected': 'Disconnected',
  'terminal.controlHeld': 'Someone else holds input control',
  'terminal.controlYours': 'You hold input control',
  'terminal.recording': 'Recording',
  'terminal.cancelTurn': 'Cancel turn',
  'terminal.pauseAutomation': 'Pause automation',
  'terminal.terminate': 'Terminate session',
  'terminal.fullscreen': 'Fullscreen',

  /* ---- The key bar on a touch device (terminal/KeyBar.tsx) -------------- */
  'keybar.title': 'Terminal keys',
  'keybar.escape': 'Esc',
  'keybar.ctrl': 'Ctrl',
  'keybar.alt': 'Alt',
  'keybar.tab': 'Tab',
  'keybar.up': 'Arrow up',
  'keybar.down': 'Arrow down',
  'keybar.left': 'Arrow left',
  'keybar.right': 'Arrow right',
  'keybar.home': 'Home',
  'keybar.end': 'End',
  'keybar.pageUp': 'Page Up',
  'keybar.pageDown': 'Page Down',
  'terminal.searchAt': '{at}/{total}',
  'terminal.search': 'Search the history',
  'terminal.largePaste': 'Large paste',
  'terminal.largePasteHint': 'You are about to paste {lines} lines. Send it to the terminal?',
  'terminal.linkConfirm': 'Open link',
  'terminal.linkConfirmHint': 'Open this address from the terminal output? {url}',
  'terminal.sessionKind.managed': 'Managed',
  'terminal.sessionKind.adopted': 'Adopted',
  'terminal.sessionKind.observed': 'Observed only',
  'terminal.observedHint':
    'This session is not inside a controllable PTY. It can be watched but not typed into.',
  'terminal.regionHint':
    'Terminal output is announced live to a screen reader. Use “Search the history” to look back through it. Press F6 to move the keyboard out of the terminal, or Shift+F6 to leave backwards.',
  'terminal.sessionDetached':
    'Session detached — the work is running but nothing is reading it. No output arrives and keystrokes do not land; reconnecting.',
  'terminal.controlHeldBy': 'Input control is held by {actor}. Ask them to release it.',
  'terminal.seat': 'Pane {n} — Cmd+S then {n}',
  'terminal.more': 'More actions',
  'terminal.history': 'History',
  'terminal.historyHint':
    "The record this session's own CLI keeps: the conversation, the tool calls and what they returned. A CLI that draws a full-screen interface leaves no scrollback in the terminal.",
  'terminal.historyLive': 'Back to live',
  'terminal.historyOlder': 'Older',
  'terminal.historyLines': 'lines {from}–{to}',
  'terminal.historyDroppedLines':
    '{lines} older lines are not held in memory; the record they come from is on disk.',
  'terminal.historyMatches': '{matches} matches',
  'terminal.historyNow': 'on screen now',
  'terminal.historyExtent': 'Record window: {from}–{to}, stream ends at {end}',
  'terminal.historyDropped':
    '{bytes} bytes of older output fell out of the buffer; nothing before this point was kept.',
  'terminal.gatewayUnavailable':
    'This server runs without the terminal gateway. The session cannot be attached from here; attach with tmux on its host.',
  'session.activity.working': 'Working',
  'session.activity.waiting': 'Waiting for you',
  'session.activity.done': 'Turn finished',
  'session.activity.idle': 'Idle',
  'session.activity.ended': 'Ended',
  'session.activity.problem': 'Problem',
  'session.activity.unknown': 'Unmeasured',
  'terminal.dropImage': 'Drop the image',
  'terminal.dropImageHint':
    'It is written to the machine this session runs on, and its path is typed at the prompt. The sentence around it is yours to write.',
  'terminal.dropUploading': 'Adding {name}…',
  'terminal.dropDone': '{name} added — its path is at the prompt.',
  'terminal.dropNeedsControl': 'Take input control of this session before adding an image.',
  'terminal.dropNotImage': 'Only PNG, JPEG, GIF and WebP images can be added.',
  'terminal.dropTooLarge': 'That image is too large to add to a session.',
  'terminal.dropFailed': 'The image could not be added: {reason}',
  'approval.title': 'Approvals',
  'approval.subtitle': 'Actions waiting on a human decision',
  'approval.inbox': 'Approval inbox',
  'approval.action': 'Requested action',
  'approval.requester': 'Requested by',
  'approval.justification': 'Justification',
  'approval.affected': 'Affected resources',
  'approval.preview': 'Preview',
  'approval.rollback': 'Rollback plan',
  'approval.alternatives': 'Alternatives',
  'approval.ifDenied': 'If declined',
  'approval.policyRule': 'Policy rule',
  'approval.expires': 'Expires',
  'approval.approveOnce': 'Approve once',
  'approval.approveModified': 'Approve with changes',
  'approval.requestClarification': 'Ask for clarification',
  'approval.delegate': 'Delegate',
  'approval.cancelTask': 'Cancel the task',
  'approval.empty': 'Nothing waiting on you.',
  'approval.emptyHint': 'Requests for critical actions will appear here.',
  'approval.riskExplainer': 'This action is {level}; policy requires a human decision.',
  'host.title': 'Host Pool',
  'host.add': 'Add host',
  'host.address': 'Address',
  'host.status': 'Status',
  'host.nodeStatus': 'Agent status',
  'host.capabilities': 'Capabilities',
  'host.lastSeen': 'Last seen',
  'host.bootstrap': 'Install agent',
  'host.upgradeAgent': 'Upgrade agent',
  'host.upgradeAgentConfirm':
    'Reinstall the agent on {host} with this build? The agent restarts; running tmux sessions are not affected.',
  'host.remove': 'Remove host',
  'host.removeConfirm':
    'Remove the record for {host}? Only the Kimene record is deleted; the machine is untouched. The server refuses a host that still holds account profiles.',
  'host.agentCurrent': 'agent current',
  'host.agentBehind': 'agent differs',
  'host.agentVersionUnknown': 'agent reported no version',
  'host.agentVersion': 'Agent version',
  'host.testConnection': 'Test connection',
  'host.provisionKey': 'Provision key',
  'host.provisionKeyTitle': 'Provision an SSH key',
  'host.provisionKeyHelp':
    'Enter the password for {host} ({address}) once. Kimene generates and installs an SSH key, then connects by key from now on — the password is never stored.',
  'host.provisionKeyHint': 'Used for this one connection only; never stored.',
  'host.provisionKeySubmit': 'Install key',
  'host.password': 'Password',
  'host.keyProvisioned': 'Key installed; testing the connection…',
  'host.check.tcp': 'Network reach',
  'host.check.hostKey': 'Host key',
  'host.check.authentication': 'Authentication',
  'host.check.installPaths': 'Install paths',
  'host.check.inventory': 'Inventory',
  'host.check.sharedAccounts': 'Shared accounts root',
  'host.cliBehind': '{latest} available',
  'fleet.cliBehindCount': '{count} CLI behind',
  'fleet.totalCores': 'Cores',
  'fleet.totalMemory': 'Memory',
  'fleet.totalSessions': 'Sessions',
  'fleet.region.connection': 'Connection',
  'fleet.region.tools': 'Tools',
  'host.fingerprint': 'Pinned host key',
  'profile.title': 'Profile',
  'profile.subtitle': 'Your own account',
  'profile.identity': 'Identity',
  'profile.username': 'Username',
  'profile.role': 'Role',
  'profile.mfa': 'Second factor',
  'profile.mfaOn': 'Enrolled',
  'profile.mfaOff': 'Not enrolled',
  'profile.lastLogin': 'Last sign-in',
  'profile.displayName': 'Display name',
  'profile.displayNameHint': 'Left empty, your username is used.',
  'profile.email': 'Email',
  'profile.emailHint': 'For the record only; Kimene sends nothing to it.',
  'profile.saved': 'Saved.',
  'profile.password': 'Password',
  'profile.passwordLede': 'Changing your password ends every session except this one.',
  'profile.currentPassword': 'Current password',
  'profile.newPassword': 'New password',
  'profile.newPasswordHint': 'At least 12 characters.',
  'profile.confirmPassword': 'Confirm new password',
  'profile.changePassword': 'Change password',
  'profile.passwordChanged': 'Password changed. Your other sessions have been ended.',
  'profile.mustChange': 'A password change is required for this account.',
  'fleet.stale': 'stale',
  'fleet.col.host': 'Host',
  'fleet.col.disk': 'Disk',
  'fleet.col.sessions': 'Sessions',
  'fleet.col.lastSeen': 'Seen',
  'fleet.col.cli': 'CLI',
  'fleet.selectAll': 'Select all',
  'fleet.bulkTitle': 'Bulk actions',
  'fleet.bulkSelected': '{count} selected',
  'fleet.bulkNone': 'Select rows for a bulk action',
  'account.selectOne': 'Select the {name} account',
  'account.bulkNone': 'Select rows for a bulk action',
  'fleet.bulkClear': 'Clear selection',
  'fleet.bulkProgress': '{done}/{total} · {host}',
  'fleet.bulkDone': '{ok} done · {skipped} skipped · {failed} failed',
  'host.cliAlreadyCurrent': 'This host already runs the current version.',
  'host.cliInstallStarted': 'The CLI install has started. Progress arrives on the event stream.',
  'host.cliUpdateOne': 'Update {cli}',
  'host.cliInstallOne': 'Install {cli}',
  'host.updateCLI': 'Update CLI',
  'host.fingerprintChanged': 'Host key changed',
  'host.fingerprintChangedHint':
    'This host’s SSH key differs from the one on record. Kimene will not accept it automatically. Do not connect until you have verified the change.',
  'org.title': 'Organization',
  'org.roleTemplates': 'Role templates',
  'org.activeAgents': 'Active agents',
  'org.roleHint': 'A role is a job definition. It is never permanently bound to an account.',
  'analytics.title': 'Analytics',
  'analytics.throughput': 'Throughput',
  'analytics.modelPerformance': 'Model performance',
  'analytics.accountReliability': 'Account reliability',
  'analytics.routeSuccess': 'Route success',
  'analytics.firstPass': 'First-pass acceptance',
  'analytics.rework': 'Rework',
  'analytics.humanIntervention': 'Human intervention',
  'analytics.approvalLatency': 'Approval latency',
  'analytics.dataSource': 'Data source',
  'analytics.sampleSize': 'Sample size',
  'analytics.insufficientData': 'Not enough data',
  'analytics.insufficientDataHint':
    'A reliable result needs at least {min} samples; there are {actual}.',
  'audit.title': 'Audit Log',
  'audit.actor': 'Actor',
  'audit.action': 'Action',
  'audit.target': 'Target',
  'audit.result': 'Result',
  'audit.client': 'Client',
  'audit.correlation': 'Correlation id',
  'audit.detail': 'Detail',
  'settings.title': 'Settings',
  'settings.security': 'Security',
  'settings.routing': 'Routing weights',
  'settings.retention': 'Retention',
  'settings.sessions': 'Active sessions',
  'settings.appearance': 'Appearance',
  'settings.autonomy': 'Autonomy',
  'settings.other': 'Other settings',
  'setup.title': 'Set up Kimene',
  'setup.subtitle': 'A few steps to a working system',
  'setup.step.owner': 'Owner account',
  'setup.step.dependencies': 'Dependency health',
  'setup.step.controlHost': 'Control host',
  'setup.step.hosts': 'Add hosts',
  'setup.step.node': 'Install agent',
  'setup.step.accounts': 'Provider accounts',
  'setup.step.projects': 'Add projects',
  'setup.step.routing': 'Routing defaults',
  'setup.step.orchestrator': 'Orchestrator policy',
  'setup.step.finalCheck': 'Final check',
  'setup.bootstrapToken': 'One-time setup token',
  'setup.ownerEmail': 'Email (optional)',
  'setup.tokenHint':
    'Kimene has no identity to authenticate against until the owner account exists, so the token is written to the server’s own log and only somebody with access to the server can read it. Retrieve it with: sudo journalctl -u kimene-server -o cat | grep bootstrap_token | tail -1 — it is valid for 30 minutes; restart the service to mint a fresh one.',
  'setup.ownerHint':
    'This account owns the system. The password must be at least 12 characters and is stored with Argon2id.',
  'setup.resume': 'Setup was interrupted. Continue where you left off.',
  'auth.signIn': 'Sign in to Kimene',
  'auth.operator': 'Operator sign-in',
  'auth.heroTitle': 'Your whole AI workforce, from one command console.',
  'auth.heroLede':
    'You hold the pools — accounts, models, hosts, projects. Kimene decides which of them runs the work, and keeps every decision explainable.',
  'auth.heroEyebrow': 'Control plane',
  'auth.heroThesis': 'One route, one decision, one record — for every execution.',
  'auth.routeAccount': 'Account',
  'auth.routeModel': 'Model',
  'auth.routeHost': 'Host',
  'auth.routeProject': 'Project',
  'auth.routeRole': 'Role',
  'auth.routeEligibility': 'Eligibility',
  'auth.routeDecision': 'Decision',
  'auth.routeRecord': 'Record',
  'auth.routeUnmeasured': 'Unmeasured',
  'auth.routeExec': 'Execution',
  'auth.routeLive': 'Live',
  'auth.username': 'Username',
  'auth.password': 'Password',
  'auth.totp': 'Verification code',
  'auth.invalidCredentials': 'That username or password is wrong.',
  'auth.rateLimited': 'Too many attempts. Try again in {seconds} seconds.',
  'auth.sessionExpired': 'Your session ended. Sign in again.',
  'confidence.EXACT': 'Measured',
  'confidence.ESTIMATED': 'Estimated',
  'confidence.INFERRED': 'Inferred',
  'confidence.UNKNOWN': 'Unknown',
  'confidence.EXACT.hint': 'Based on structured data from the provider.',
  'confidence.ESTIMATED.hint': 'Computed from usage history.',
  'confidence.INFERRED.hint': 'Deduced indirectly; not certain.',
  'confidence.UNKNOWN.hint': 'No reliable signal. This value has not been measured.',
  'risk.L0_READ_ONLY': 'Read only',
  'risk.L1_ISOLATED_WRITE': 'Isolated write',
  'risk.L2_VALIDATED_CHANGE': 'Validated change',
  'risk.L3_EXTERNAL_OR_IRREVERSIBLE': 'External / irreversible',
  'risk.L4_FINANCIAL_OR_SECURITY': 'Financial / security',

  // ---- Shared vocabulary ----
  'board.backlog': 'Backlog',
  'board.routing': 'Routing',
  'board.blocked': 'Blocked',
  'board.review': 'In review',
  'board.done': 'Done',
  'mission.noSchedule': 'No task carries timing information yet.',
  'mission.project': 'Project',
  'common.actions': 'Actions',
  'common.all': 'All',
  'common.none': 'None',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.filters': 'Filters',
  'common.clearFilters': 'Clear filters',
  'common.unmeasured': 'Not recorded — unmeasured, not zero.',
  'common.loadMore': 'Load more',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.cards': 'Cards',
  'common.table': 'Table',
  'common.view': 'View',
  'common.selectPrompt': 'Choose a record from the list to see its detail.',
  'common.noMatches': 'No records match these filters.',
  'common.noMatchesHint': 'Relax or clear the filters.',
  'common.count': '{count} records',
  'common.id': 'Id',
  'common.name': 'Name',
  'common.type': 'Type',
  'common.category': 'Category',
  'common.created': 'Created',
  'common.updated': 'Updated',
  'common.project': 'Project',
  'common.task': 'Task',
  'common.expand': 'Expand',
  'common.collapse': 'Collapse',
  'common.exitFullscreen': 'Exit fullscreen',
  'common.required': 'Required',
  'common.optional': 'Optional',
  'common.saving': 'Saving',
  'common.saved': 'Saved',
  'common.source': 'Source',
  'common.latency': 'Latency',
  'common.reason': 'Reason',
  'common.enabled': 'Enabled',
  'common.disabled': 'Disabled',
  'common.searchPlaceholder': 'Search this list',
  'common.notMeasured': 'Not measured',
  'common.version': 'Version',
  'common.time': 'Time',
  'common.actionFailed': 'The action could not be completed.',

  // ---- Command centre ----
  'cc.lastCheckpoint': 'Last checkpoint',

  // ---- Command centre: executor panel ----
  'cc.noExecutors': 'No executor is rented',
  'cc.noExecutorsHint':
    'When the orchestrator runs a task, the session it rents appears here. The orchestrator itself has not stopped; it is simply not executing anything right now.',
  'cc.safeToSwitch': 'Safe to switch executor',
  'cc.unsafeToSwitch': 'Not safe to switch',
  'cc.pendingHandoffs': 'Pending handoffs',
  'cc.awaitingApproval': 'Awaiting approval',
  'cc.noCheckpoint': 'No checkpoint',
  'cc.noAccountAssigned': 'No account assigned',
  'cc.orchestratorUnavailable': 'The orchestrator status could not be read.',
  'cc.orchestratorUnavailableHint':
    'It is retried every thirty seconds. Until then which executor is running is unknown — not absent.',

  // ---- Accounts ----
  'account.empty': 'No accounts defined.',
  'account.emptyHint': 'The router cannot run without at least one provider account.',
  'account.matrixHint': 'Rows are accounts, columns are models. Select a cell for its detail.',
  'account.matrixEmpty': 'Not enough data to draw the matrix.',
  'account.matrixEmptyHint': 'At least one account and one model must exist.',
  'account.neverVerified': 'Never verified',
  'account.preferred': 'Preferred',
  'account.imported': 'Imported',
  'account.probeResult':
    'Probe finished: {verified} reachable, {unavailable} unreachable, {unverified} unverified.',
  'account.probeDetail': 'Provider response',
  'account.openLoginHint':
    'The sign-in terminal opens on the host; credentials never reach the browser.',
  'account.availability.AVAILABLE': 'Available',
  'account.availability.UNAVAILABLE': 'Unavailable',
  'account.availability.UNVERIFIED': 'Unverified',
  'account.availability.DEGRADED': 'Degraded',
  'account.availability.BLOCKED': 'Blocked',
  'account.availability.NA': 'Not applicable to this provider',

  // ---- Models ----
  'model.empty': 'No models defined.',
  'model.emptyHint': 'Models arrive through provider discovery. Add an account and run a probe.',
  'model.externalId': 'Provider id',
  'model.discoverySource': 'Discovery source',
  'model.lastDiscovered': 'Last discovered',
  'model.contextUnknown': 'Context window not reported',
  'model.cap.tools': 'Tools',
  'model.cap.vision': 'Vision',
  'model.cap.structured': 'Structured output',
  'model.cap.longContext': 'Long context',
  'model.codeCapability': 'Code capability',
  'model.tokens': '{count} tokens',
  'model.maxOutput': 'Max output:',

  // ---- Hosts ----
  'host.empty': 'No hosts defined.',
  'host.emptyHint': 'Kimene runs work on hosts. Add one to begin.',
  'host.controlHost': 'Control host',
  'host.os': 'Operating system',
  'host.arch': 'Architecture',
  'host.cores': 'Cores',
  'host.memory': 'Memory',
  'host.disk': 'Disk',
  'host.gpu': 'GPU',
  'host.tooling': 'Tool inventory',
  'host.installed': 'Installed',
  'host.missing': 'Missing',
  'host.user': 'User',
  'host.port': 'Port',
  'host.labels': 'Labels',
  'host.labelsPlaceholder': 'build, gpu',
  'host.addHint':
    'Define a host reachable over SSH. Kimene stores no passwords; access is key-based.',
  'host.testResult': 'Last connection test',
  'host.checkSkipped': 'Skipped',
  'host.authMethod': 'Authentication method',
  'host.fingerprintNone': 'None on record',
  'host.fingerprintUnknown':
    'No key is on record for this host, so nothing was verified. Compare it against the machine itself:',
  'host.bootstrapStarted': 'Agent installation started.',
  'host.fingerprintExpected': 'Fingerprint on record',
  'host.fingerprintObserved': 'Fingerprint observed',
  'host.fingerprintBlocked': 'Connections to this host are blocked',
  'host.capabilitiesUnknown': 'Capabilities have not been discovered yet.',
  'host.nodeVersion': 'Agent version',
  'host.kernel': 'Kernel',
  'host.noSample': 'no sample',
  'host.staleFor': 'stale {rel}',
  'host.neverSeen': 'never',
  'host.noGpu': 'no GPU',
  'host.hiddenInList': 'This host is hidden from the list by the current filters.',
  'host.notDiscovered': 'Inventory has not been discovered yet.',

  'fleet.title': 'Servers',
  'fleet.nodes': '{count} nodes',
  'fleet.online': '{count} online',
  'fleet.shown': '{shown}/{total} shown',
  'fleet.region.fleet': 'Server fleet',
  'fleet.region.focus': 'Focus',
  'fleet.region.liveMetrics': 'Live metrics',
  'fleet.region.gpu': 'GPU bay',
  'fleet.region.inventory': 'Inventory',
  'fleet.region.sort': 'Sort',
  'fleet.region.filter': 'Filter',
  'fleet.region.status': 'Status',
  'fleet.region.pressure': 'Pressure',
  'fleet.reporting': '{n}/{m} reporting',
  'fleet.peak': 'peak {pct}%',
  'fleet.inventoried': '{n}/{m} inventoried',
  'fleet.updated': 'refreshed',
  'fleet.stream': 'stream',
  'fleet.pressure.calm': 'calm',
  'fleet.pressure.warn': 'tight',
  'fleet.pressure.crit': 'critical',
  'fleet.pressure.unmeasured': 'unmeasured',
  'fleet.sort.health': 'Health',
  'fleet.sort.name': 'Name',
  'fleet.sort.pressure': 'Load',
  'fleet.sort.cpu': 'CPU',
  'fleet.sort.ram': 'RAM',
  'fleet.sort.disk': 'Disk',
  'fleet.sort.gpu': 'GPU',
  'fleet.sort.load': 'Load avg.',
  'fleet.sort.sessions': 'Sessions',
  'fleet.sort.lastSeen': 'Last seen',
  'fleet.sort.loadShort': 'Load',
  'fleet.sort.lastSeenShort': 'Seen',
  'fleet.sortDir': 'Sort direction',
  'fleet.sortAsc': 'Ascending',
  'fleet.sortDesc': 'Descending',
  'fleet.search': 'Search hosts',
  'fleet.searchPlaceholder': 'name, address, label…',
  'fleet.filter.gpu': 'GPU',
  'fleet.filter.stale': 'Stale',
  'fleet.filter.control': 'Control',
  'fleet.clear': 'Clear',
  'fleet.control': '#control',
  'fleet.selectHost': 'Select a host to see its detail.',
  'fleet.back': 'Fleet',
  'fleet.staleData': 'Metrics could not be refreshed; showing the last known values.',
  'fleet.noMatches': 'No hosts match the filters.',
  'fleet.focus.sessions': 'Sessions',
  'fleet.focus.accounts': 'Accounts',

  // ---- Projects ----
  'project.empty': 'No projects yet.',
  'project.emptyHint':
    'Start by adding a repository; Kimene scans the stack and configuration itself.',
  'project.slug': 'Slug',
  'project.configFile': 'File',
  'project.configPresent': 'Present',
  'project.configMissing': 'Absent',
  'project.configHash': 'Content hash',
  'project.configNotShown': 'Contents not shown',
  'project.configNotShownHint':
    'These files can carry secrets, so Kimene never sends their contents to the browser.',
  'project.lastScan': 'Last scan',
  'project.fingerprint': 'Configuration fingerprint',
  'project.noRepositories': 'No repository is attached to this project.',
  'project.detectedStack': 'Detected stack',
  'project.branch': 'Default branch',
  'project.remote': 'Remote',
  'project.path': 'Path',
  'project.newTitle': 'New project',
  'project.create': 'Create',
  'project.name': 'Project name',
  'project.namePlaceholder': 'e.g. Kimene',
  'project.descriptionHint': 'Optional short description',
  'project.selectHost': 'Choose a server',
  'project.controlHost': 'control',
  'project.pathPlaceholder': '/srv/kimene',
  'project.pathHint': 'Absolute directory path on the server',
  'project.repoName': 'Repository name (optional)',
  'project.repoNameHint': 'Derived from the path/project name if blank',
  'project.addRepository': 'Add repository',
  'project.addRepositoryTitle': 'Add a repository',
  'project.removeRepository': 'Remove repository',
  'project.removeRepositoryConfirm': 'Remove the {name} repository from this project?',
  'project.noHosts': 'Add a server on the Servers page first.',
  'project.noRepositoriesHint': 'Add a repository: a server and an absolute directory on it.',
  'project.noDecisions': 'No routing decision has been made for this project yet.',
  'project.noSessions': 'No session is open for this project.',
  'project.noArtifacts': 'This project has produced no artifacts.',
  'project.noPerformance': 'Not enough data for model performance.',

  'project.tab.overview': 'Overview',
  'project.tab.repositories': 'Repositories',
  'project.tab.sessions': 'Sessions',
  'project.tab.audit': 'Audit',
  'project.backToList': 'Projects',
  'project.identity': 'Identity',
  'project.policy': 'Policy',
  'project.routingNote':
    'A routing policy states preference and permission. It binds no account and no model to the project.',
  'project.hosts': 'Hosts',
  'project.location': 'Location',
  'project.recentSessions': 'Recent sessions',
  'project.gitRepositories': 'Git repositories',
  'project.openSessions': 'Open sessions',
  'project.timezone': 'Time zone',
  'project.created': 'Created',
  'project.updated': 'Updated',
  'project.recordVersion': 'Record version',
  'project.qualityReviewer': 'Independent reviewer',
  'project.qualityCrossProvider': 'Different provider required',
  'project.routingMode': 'Routing mode',
  'project.allowProviderSwitch': 'Provider switching',
  'project.allowModelSwitch': 'Model switching',
  'project.safeCheckpointOnly': 'Only at a safe boundary',

  'project.scan': 'Read repositories',
  'project.scanOne': 'Read this repository',
  'project.scanning': 'Reading',
  'project.scanNever': 'This repository has never been read.',
  'project.scanNeverHint':
    '“Read repositories” takes the git facts, the stack and the configuration files from the host itself.',
  'project.scanFailed': 'The repository could not be read',
  'project.scanRead': 'Read',
  'project.search': 'Search project, host, branch, stack',
  'project.filter.all': 'All',
  'project.filter.attention': 'Needs attention',
  'project.filter.running': 'Running',
  'project.filter.unconfigured': 'Unconfigured',
  'project.noMatches': 'No project matches the search.',
  'project.noMatchesHint': 'Clear the search or choose another filter.',
  'project.matchCount': '{shown} of {total} projects',
  'project.treeClean': 'clean',
  'project.treeDirty': 'uncommitted changes',
  'project.treeUnknown': 'tree state unread',
  'project.onHost': 'on {host}',
  'project.hostsN': '{count} hosts',
  'project.branchesN': '{count} branches',
  'project.unreadN': '{count} repositories never scanned',
  'project.brokenN': '{count} repositories cannot be read',
  'project.liveSessions': '{count} open sessions',
  'project.agentClaude': 'Claude instructions',
  'project.agentCodex': 'Codex instructions',
  'project.agentKimene': 'Kimene file',
  'project.credentialOverrideN': '{count} settings override the account',
  'project.credentialOverrideHint': 'Every session opened in this directory runs on the credential this project setting names, whatever account Kimene chose.',
  'project.credentialOverrideTitle': 'Settings that override the account',
  'project.credentialOverrideBody': 'The session’s credential comes from these files, not from the account. Only the setting’s name is read — its value never leaves the host.',
  'project.agentNone': 'No agent instructions',
  'project.agentNoneHint':
    'This project has no CLAUDE.md, AGENTS.md or .codex. An agent started here begins with no project-specific instructions at all.',
  'project.lastCommit': 'Last commit',
  'project.openProject': 'Open project',

  'project.whereItRuns': 'Where it runs',
  'project.agentInstructions': 'Agent instructions',
  'project.agentPresentHint':
    'An agent started here begins by reading these instruction files from the checkout.',

  'project.scanUnread': 'Unread',
  'project.scanStale': 'These facts were read {when}.',
  'project.scanResult': '{read} read, {failed} unreadable.',
  'project.scanNoRepositories': 'There is no repository to read; add one first.',

  'project.git': 'Git',
  'project.gitYes': 'Git repository',
  'project.gitNo': 'Not a git repository',
  'project.gitUnknown': 'Git state not read',
  'project.gitMissingOnHost': 'The host has no git, so the git facts cannot be read.',
  'project.currentBranch': 'Current branch',
  'project.head': 'Last commit',
  'project.headAuthor': 'Committed by',
  'project.dirty': 'Uncommitted changes',
  'project.dirtyCount': '{count} files changed',
  'project.clean': 'Working tree clean',
  'project.dirtyUnknown': 'Change state not read',
  'project.pathMissing': 'The directory was not found on the host',
  'project.pathMissingHint':
    'Kimene could not find this path on {host}. It may have been moved or removed.',
  'project.gitRootDiffers': 'The repository root is another directory: {path}',
  'project.worktrees': 'Worktrees',
  'project.worktreePrimary': 'primary',
  'project.worktreeDetached': 'detached',
  'project.worktreeLocked': 'locked',
  'project.worktreePrunable': 'prunable',
  'project.commands': 'Commands',
  'project.buildCommand': 'Build',
  'project.testCommand': 'Test',
  'project.lintCommand': 'Lint',
  'project.worktreeRoot': 'Worktree root',
  'project.commandsUnset':
    'These are entered by hand; the scan does not guess them. The quality gates depend on them.',
  'project.configInventoryTitle': 'Configuration inventory',
  'project.configFingerprintHint':
    'The fingerprint is derived from the digests of the files below. If it moves, what the agent is told has changed.',
  'project.configFingerprintUnknown': 'No fingerprint: the host could not compute content digests.',

  'project.edit': 'Edit project',
  'project.state': 'State',
  'project.stateHint':
    'ONBOARDING holds work back; ACTIVE accepts it; PAUSED and ARCHIVED stop it.',
  'project.activate': 'Accept work',
  'project.saved': 'Saved.',

  'project.delete': 'Delete project',
  'project.deleteTitle': 'Delete {name}?',
  'project.deleteMeaning':
    'This removes only Kimene’s record of the project. The directories, repositories and files on the host are left exactly as they are.',
  'project.deleteChecks': 'Pre-flight',
  'project.deleteConsequences': 'What removal costs',
  'project.deleteReady': 'Nothing blocks the delete.',
  'project.deleteBlockedHint': 'The project cannot be removed until these are resolved.',
  'project.deleteRecheck': 'Check again',
  'project.deleteBlocker.activeSession':
    'A session is open on {host} ({provider}, {state}). Stop it first.',
  'project.deleteNote.repositories':
    'Kimene forgets {count} repository records: {paths}. The directories are not touched.',
  'project.deleteNote.sessionHistory':
    '{count} past sessions keep referring to this project and stay readable.',
  'project.deleteNote.projectActive': 'This project is currently accepting work.',
  'project.deleted': 'The project was deleted.',

  'project.sessionsHistory': 'Sessions in this project (history included)',
  'project.noSessionsEver': 'No session has ever run in this project.',
  'project.noAudit': 'There is no audit record for this project.',
  'project.openInCockpit': 'Open the session',

  // ---- Missions and tasks ----
  'mission.empty': 'No missions yet.',
  'mission.emptyHint':
    'A mission is the container that splits an objective into tasks. Create the first one.',
  'mission.priority': 'Priority',
  'mission.deadline': 'Deadline',
  'mission.graphLoading': 'Loading the graph',
  'mission.noTasks': 'This mission has no tasks yet.',
  'mission.criticalPathHint':
    'The critical path is the longest dependency chain; it sets the mission’s finish time.',
  'mission.selectPrompt': 'Choose a mission to see its detail.',
  'task.account': 'Account',
  'task.model': 'Model',
  'task.host': 'Host',
  'task.project': 'Project',
  'task.routeScore': 'Route score',
  'task.noRoute': 'No route assigned',
  'task.noSession': 'No open session',

  // ---- Live operations ----
  'live.empty': 'No open sessions.',
  'live.emptyHint':
    'When a task starts running, its session appears here. Sessions that have ended stay in the list for a while.',
  'live.selectSession': 'Choose a session to see its terminal.',
  'live.signInConfirming': 'Confirming the sign-in…',
  'live.signInConfirmed': 'Sign-in confirmed. The account is ready for work.',
  'live.signInNotReady':
    'The sign-in could not be confirmed yet. Finish the flow in the terminal and try again.',
  'live.signInUnconfirmed':
    'The sign-in pane closed. Whether the credential landed has not been confirmed yet.',
  'live.signInConfirmAction': 'Confirm the sign-in',
  'live.signInAttemptFailed':
    'The sign-in attempt did not complete — the pasted code was not issued for this pane. Every sign-in makes its own link; take the code from this pane\'s link. The account\'s own state is checked below.',
  'live.layoutSingle': 'Single',
  'live.layoutSplit': 'Split',
  'live.layoutGrid': 'Grid',

  'live.selectOne': 'Select the {name} session',
  'live.bulkShowInGrid': 'Show in grid',
  'live.bulkRestart': 'Restart',
  'live.bulkRestartConfirm': 'Restart {count} sessions? Work in progress is lost.',
  'live.bulkRestartConfirmSkipping':
    '{count} sessions will restart. {skipped} are working right now and will be skipped — cutting an agent off mid tool call leaves the work half done.',
  'live.bulkStop': 'Stop',
  'live.bulkStopConfirm': 'Stop {count} sessions? Work in progress is lost.',
  'live.bulkClear': 'Remove from list',
  'live.bulkClearConfirm': 'Remove {count} ended sessions from the list?',

  'order.move': 'Move the {name} row',
  // --- Recording playback ---------------------------------------------------
  'rec.title': 'Recording',
  'rec.note': 'The only history a finished session has. Rendered to lines on the server.',
  'rec.notRedacted': 'This recording was written without redaction: it holds whatever was typed, as it was typed.',
  'rec.geometryUnknown':
    'This recording carries no geometry; it was rendered at {cols}×{rows}, so lines may sit differently than they did.',
  'rec.alternateScreen':
    'This session drew on the alternate screen, where nothing scrolls away — so there is no history to show. Its last screen is below.',
  'rec.lastScreen': 'The last screen',
  'rec.open': 'Open the recording',

  // --- Files ----------------------------------------------------------------
  'files.tab': 'Files',
  'files.repository': 'Repository',
  'files.noRepos': 'This project has no registered repository.',
  'files.noReposHint': 'Register a checkout on the Repositories tab; files are read from its root.',
  'files.empty': 'This directory is empty.',
  'files.emptyHint': 'The root is the repository’s own path, and nothing reaches outside it.',
  'files.truncated': 'This directory holds more than one listing carries; some of it is not shown.',
  'files.outside': 'leads out of the root',
  'files.newFolder': 'Folder',
  'files.newFolderName': 'Folder name',
  'files.remove': 'Delete {name}',
  'files.removeConfirm': 'Delete {name}? This cannot be undone.',
  'files.removeDirConfirm':
    'Delete {name}? Only if it is empty — this will not empty a directory for you.',
  'files.binary': 'This file is not text. Decided on its bytes, not its name.',

  // --- Shell templates -----------------------------------------------------
  'tpl.delete': 'Delete the {name} template',
  'tpl.group': 'Templates',
  'tpl.command': 'First command',
  'tpl.commandHint': 'Typed once the shell is ready — as if by hand, visible on screen.',
  'tpl.saveAs': 'Save as a template',
  'tpl.saveAsHint': 'Give it a name and it appears in the palette (F2). Kept in this browser only.',

  // --- Keyboard shortcuts ---------------------------------------------------
  'keys.title': 'Keyboard shortcuts',
  'keys.lede': 'All of these already worked; the ones nobody wrote down were the ones nobody found.',
  'keys.groupConsole': 'Console',
  'keys.groupPane': 'Terminal pane',
  'keys.groupWall': 'The wall',
  'keys.palette': 'Open the command palette',
  'keys.dismiss': 'Dismiss',
  'keys.leavePane': 'Leave the pane (take the keyboard back)',
  'keys.forceSelect': 'While dragging: select even though the application tracks the mouse',
  'keys.copyMove': 'Move the cursor',
  'keys.copyYank': 'Take it and leave',
  'keys.dragStart': 'Start a drag, and drop it',
  'keys.dragMove': 'Move while dragging',
  'keys.spanResize': 'Grow or shrink the tile',
  'keys.exitFull': 'Leave fullscreen',
  'keys.paneFull': 'Maximise the focused pane, and put it back',
  'keys.seat': 'Go to a pane by its number (the wall’s order)',
  'keys.control': 'Take control / hand the keyboard back',
  'keys.historyOpen': 'Open the session’s record',
  'keys.backToLive': 'Back to live',
  'keys.scopeRecord': 'with the record open',
  'keys.scopeWall': 'on the wall',
  'keys.help': 'Show keyboard shortcuts',
  'keys.groupPalette': 'Command palette',
  'keys.groupCopy': 'Copy mode',
  'keys.paletteMove': 'Move through the list',
  'keys.paletteRun': 'Run the selected command',
  'keys.leavePaneBack': 'Leave the pane backwards',
  'keys.findNext': 'Next match',
  'keys.findPrev': 'Previous match',
  'keys.copyLineEnds': 'Start / end of line',
  'keys.copyPage': 'Page up / down',
  'keys.copyHalfPage': 'Half page up / down',
  'keys.copyAnchorLine': 'Start a line selection (press again to drop it)',
  'keys.copyAnchorChar': 'Start a character selection (press again to drop it)',
  'keys.copyLeave': 'Leave copy mode',
  'keys.copyVim': 'The same moves, in vim keys',
  'keys.scopePalette': 'while the palette is open',
  'keys.scopeFind': 'while the find box is open',
  'keys.scopeAnywhere': 'anywhere',
  'keys.files': 'Open or close the files panel',
  'keys.scopeOverlay': 'in an open overlay',
  'keys.scopePane': 'inside a pane',
  'keys.scopeCopy': 'in copy mode',
  'keys.scopeGrip': 'with a grip focused',
  'keys.scopeDragging': 'while dragging',
  'keys.scopeSpan': 'with a resize corner focused',
  'keys.scopeFull': 'with a pane fullscreen',

  // --- Copy mode -----------------------------------------------------------
  'copy.mode': 'Copy',
  'copy.leave': 'Leave copy mode',
  'copy.hint': 'Select with the keyboard and take it (vim keys)',
  'copy.char': 'characters',
  'copy.line': 'lines',
  'copy.yank': 'take',
  'copy.cursor': 'cursor',
  'copy.taken': '{n} characters copied',

  // --- Command palette -----------------------------------------------------
  'palette.title': 'Command palette',
  'palette.open': 'Command palette (F2)',
  'palette.placeholder': 'Type an action…',
  'palette.none': 'No matching action',
  'palette.groupOpen': 'Open a shell',
  'palette.groupPanes': 'Panes',
  'palette.groupLayouts': 'Saved layouts',
  'palette.groupView': 'View',
  'palette.openOn': 'Open a shell on {host}',
  'palette.show': 'Show on the wall',
  'palette.hide': 'Take off the wall',
  'palette.close': 'Close the session',

  // --- Broadcast: type once, reach several shells ---------------------------
  'cast.on': 'Broadcast',
  'cast.off': 'Stop broadcasting',
  'cast.hint': 'Every key you press goes to every listening pane.',
  'cast.count': '{n} panes listening',
  'cast.none': 'No panes listening',
  'cast.mute': 'Take this pane out of the broadcast',
  'cast.unmute': 'Put this pane in the broadcast',
  'cast.armed': 'Broadcast on — {n} panes listening',

  'ssh.showShells': 'Show the shells',
  'ssh.hideShells': 'Hide the shells',
  'ssh.resize': 'Resize the shells column',
  'ssh.title': 'Terminals',
  'ssh.subtitle': 'Shells on machines — no agent, no account',
  'ssh.open': 'Open a shell',
  'ssh.open.title': 'Open shells',
  'ssh.wall': 'Shell wall',
  'ssh.wallEmpty': 'Pick a shell to see it.',
  'ssh.wallEmptyHint': 'Click one in the list on the left, or drag it onto the wall.',
  'ssh.empty': 'No shells open.',
  'ssh.emptyHint': 'Use "Open a shell" to connect to a machine.',
  'ssh.host': 'Host',
  'ssh.hostPick': 'Pick a host',
  'ssh.label': 'Name',
  'ssh.labelHint': 'Shown in the list and in the tmux name. The host\'s name if left empty.',
  'ssh.workingDir': 'Starting directory',
  'ssh.workingDirHint': 'An absolute path. The account\'s home if left empty.',
  'ssh.record': 'Record this shell',
  'ssh.recordHint':
    'A recording is raw terminal bytes: it keeps everything typed, including a password pasted at a prompt. It is the only history a session leaves once it has ended; only an Administrator or Owner can read one, and it is deleted when its retention window closes.',
  'ssh.noHosts': 'No hosts are online.',
  'ssh.closeMany': 'Close',
  'ssh.closeManyConfirm': 'Close {count} shells? Work in progress inside them is lost.',
  'order.select': 'Select rows',
  'order.selectDone': 'Done selecting',
  'order.selectAllShown': 'Select all shown',
  'order.manual': 'Your order',
  'order.reset': 'Back to the automatic order',
  'live.wallMove': 'Move on the wall',
  'live.wallResize': 'Pane size',
  'live.wallLayouts': 'Layouts',
  'live.wallSave': 'Save this layout',
  'live.wallSavePrompt': 'Layout name',
  'live.wallLost': '{count} of this layout\'s panes are gone: {names}',
  'live.wallDelete': 'Delete the {name} layout',
  'live.wallLocal': 'The layout is kept in this browser.',
  'live.wallEmpty': 'No saved layouts.',
  'live.wallDrifted': 'The wall differs from this saved layout; the tab brings it back.',
  'live.wallPending': 'Connects when it comes into view',
  'order.dragStart': 'Picked up {name}. Arrow keys to place, space to drop.',
  'order.dragOver': 'Over {name}.',
  'order.dragEnd': 'Dropped at {name}.',
  'order.dragCancel': 'Move cancelled.',
  'live.layout': 'Terminal layout',
  'live.noMessages': 'No visible messages.',
  'live.noToolActivity': 'No tool activity.',
  'live.noChangedFiles': 'No changed files.',
  'live.noDiff': 'No diff to show.',
  'live.noTests': 'No test run recorded.',
  'live.noMetrics': 'No metrics.',
  'live.turns': 'Turns',
  'live.workingDir': 'Working directory',
  'live.branch': 'Branch',
  'live.waitingReason': 'Waiting for',
  'live.testsPassed': 'Passed',
  'live.testsFailed': 'Failed',
  'live.testsSkipped': 'Skipped',
  'live.sessionKind': 'Session kind',
  'live.kindHint.managed': 'Kimene started it and holds it: you can stop it, restart it and take the keyboard.',
  'live.kindHint.adopted': 'Kimene did not start it but took it over with your consent: it can be driven like a managed one.',
  'live.kindHint.observed': 'Kimene can see it but has no controllable end: it is watched only, and cannot be typed into.',
  'live.tmuxSession': 'tmux session',
  'live.toolCalls': 'Tool calls (ok / failed)',
  'live.endedAt': 'Ended',
  'live.endedAgo': 'ended · {when}',
  'live.endedFailed': 'failed · {when}',
  'live.checkpoints': 'Checkpoints',
  'live.checkpointSummary': 'Summary',
  'live.noCheckpoints': 'This session has no checkpoint.',
  'live.safeBoundary': 'Safe boundary',
  'live.repositoryState': 'Repository state',
  'live.dirtyFiles': '{count} changed files',
  'live.timelineLiveOnly':
    'This timeline shows events from the moment the console connected. No session history is served.',
  'live.truncated':
    'The server returned the first page of the list. The filters here apply to that page only, so other sessions may match.',

  // ---- Approvals ----
  'approval.filterAction': 'Action type',
  'approval.filterAge': 'Age',
  'approval.age.1h': 'Last hour',
  'approval.age.24h': 'Last 24 hours',
  'approval.age.7d': 'Last 7 days',
  'approval.age.older': 'Older than 7 days',
  'approval.selectPrompt': 'Choose a request to decide on it.',
  'approval.reviewerFindings': 'Reviewer findings',
  'approval.modifications': 'Modified action',
  'approval.modificationsHint': 'Write the exact form to approve. The agent runs precisely this.',
  'approval.clarificationQuestion': 'Your question',
  'approval.delegateTo': 'Delegate to',
  'approval.rejectReason': 'Reason for rejection',
  'approval.decidedBy': 'Decided by',
  'approval.decidedAt': 'Decided at',
  'approval.decideFailed': 'The decision could not be recorded.',
  'approval.noPreview': 'No preview available.',
  'approval.expiresSoon': 'Expiring soon',
  'approval.reasonRequired': 'This decision needs a reason.',

  // ---- Organisation ----
  'org.owner': 'Owner',
  'org.executiveSupervisor': 'Executive Supervisor',
  'org.projectDirectors': 'Project Directors',
  'org.functionalManagers': 'Functional Managers',
  'org.specialists': 'Specialists',
  'org.auditor': 'Auditor',
  'org.chart': 'Chart',
  'org.template': 'Template',
  'org.instance': 'Running instance',
  'org.noInstances': 'No running instance of this role.',
  'org.instanceCount': '{count} running',
  'org.emptyRoles': 'No role templates.',
  'org.emptyRolesHint': 'Roles are the organisation’s job definitions, created during setup.',
  'org.autonomy': 'Autonomy',
  'org.maxRisk': 'Maximum risk',
  'org.tools': 'Granted tools',
  'org.reportsTo': 'Reports to',
  'org.category': 'Category',
  'org.uncategorised': 'Uncategorised',
  'org.categoryHint':
    'The chart bands roles by category. Kimene has no fixed hierarchy: a role’s superior is its escalation path.',
  'org.escalation': 'Escalation path',
  'org.escalatesTo': 'Escalates to',
  'org.allowedTools': 'Allowed tools',
  'org.deniedCount': '{count} denied',

  // ---- Analytics ----
  'analytics.taskDuration': 'Task duration',
  'analytics.providerFailures': 'Provider failures',
  'analytics.hostUtilisation': 'Host utilisation',
  'analytics.rolePerformance': 'Role performance',
  'analytics.window': 'Time window',
  'analytics.window.24h': '24 hours',
  'analytics.window.7d': '7 days',
  'analytics.window.30d': '30 days',
  'analytics.window.90d': '90 days',
  'analytics.noCost': 'No monetary cost is shown',
  'analytics.noCostHint':
    'Provider billing is not visible to Kimene, and an invented figure would be worse than none.',
  'analytics.empty': 'Nothing has been measured yet.',
  'analytics.emptyHint': 'This screen fills up as tasks complete.',
  'analytics.completedTasks': 'Completed tasks',
  'analytics.perDay': 'per day',

  // ---- Analytics: what /analytics/summary actually sends ----
  'analytics.asOf': 'As of',
  'analytics.noWindow': 'This screen is a snapshot',
  'analytics.noWindowHint':
    'The server counts these at request time; no time window is applied. "3 failed" means three tasks are failed right now, not three failures this week.',
  'analytics.unqualified': 'No sample size reported',
  'analytics.source.liveCount': 'live record count',
  'analytics.source.decisions': 'recent routing decisions',

  'analytics.tasks': 'Tasks',
  'analytics.tasksActive': 'Active',
  'analytics.tasksBlocked': 'Blocked',
  'analytics.tasksNeedsHuman': 'Waiting on a person',
  'analytics.tasksFailed': 'Failed',

  'analytics.accounts': 'Account pool',
  'analytics.accountsTotal': 'Accounts total',
  'analytics.accountsReady': 'Can take work',
  'analytics.accountsAuthRequired': 'Needs sign-in',
  'analytics.accountsExhausted': 'Quota exhausted',
  'analytics.accountsDisabled': 'Disabled',
  'analytics.accountsUnclassified': 'Unclassified',
  'analytics.accountsHint':
    'The server counts only these four states; an unconfigured or degraded account falls into none of them and is shown as unclassified.',

  'analytics.hosts': 'Hosts',
  'analytics.hostsTotal': 'Hosts total',
  'analytics.hostsOnline': 'Online',
  'analytics.hostsOffline': 'Offline',
  'analytics.hostsUnclassified': 'Unclassified',

  'analytics.routing': 'Routing',
  'analytics.decisions': 'Decisions',
  'analytics.unrouted': 'Unrouted',
  'analytics.routedShare': 'Routed share',
  'analytics.meanDecisionTime': 'Mean decision time',
  'analytics.providerMix': 'Provider mix',
  'analytics.noProviderMix': 'No decision selected a provider, so there is no mix to show.',
  'analytics.simulatedExcluded':
    'Simulated decisions are excluded; counting them would let a UI refresh move the numbers an operator is reading.',

  'analytics.approvals': 'Approvals',
  'analytics.approvalsOpen': 'Open approvals',

  // ---- Audit ----
  'audit.empty': 'The audit log is empty.',
  'audit.emptyHint': 'Every administrative action is written here. None has happened yet.',
  'audit.immutable': 'This log is append-only: no row can be altered or removed.',
  'audit.result.SUCCESS': 'Success',
  'audit.result.FAILURE': 'Failure',
  'audit.result.DENIED': 'Denied',
  'audit.clientIp': 'Client address',
  'audit.targetType': 'Target type',
  'audit.exactMatch': 'Matched exactly; a partial value finds nothing.',
  'audit.actorPlaceholder': 'user:koraykaradas',
  'audit.actionPlaceholder': 'auth.login',
  'audit.targetTypePlaceholder': 'host',
  'audit.pageStatus': 'Page {page} · {count} entries',
  'audit.page': 'Page {page}',
  'audit.perPage': 'Rows per page',
  'audit.perPageOption': '{count} / page',

  // ---- Settings ----
  'settings.keyCount': '{count} keys',
  'settings.saved': 'Saved.',
  'settings.unsaved': 'Unsaved',
  'settings.changed': 'changed',
  'settings.revert': 'Revert',

  'settings.storedOnly': 'nothing reads this',
  'settings.storedOnlyHint':
    'This document is stored, versioned and audited — and nothing in this build reads it. Changing it here changes no behaviour today; what is in force is the default compiled into the binary.',
  'settings.rewritten':
    'Somebody else saved this setting while you had it open. The form has been reseeded from the server, so any unsaved edit was not kept — holding it would have quietly overwritten their change.',
  'settings.totp': 'Two-factor authentication',
  'settings.totpOn': 'On',
  'settings.totpOff': 'Off',
  'settings.revoke': 'Revoke',
  'settings.revokeOthers': 'Revoke every other session',
  'settings.currentSession': 'This session',
  'settings.lastActive': 'Last active',
  'settings.routingLede':
    'Relative weight of each routing score component. Weights are recorded with every decision, so past decisions stay explainable.',
  'settings.routingInert':
    'The router scores from its own built-in table today, and the component names there do not line up one-to-one with the ones in this document.',
  'settings.weightSum': 'Sum',
  'settings.weightShare': 'Share',
  'settings.weightShareHint':
    'Weights are relative; what governs a decision is each one’s share of the total.',
  'settings.normalize': 'Normalise to 1.00',
  'settings.weightAccountPreference': 'Account preference',
  'settings.weightCapacityHeadroom': 'Capacity headroom',
  'settings.weightHistoricalSuccess': 'Historical success',
  'settings.weightHostLocality': 'Host locality',
  'settings.weightLatency': 'Latency',
  'settings.weightModelSuitability': 'Model suitability',
  'settings.weightReviewerDiversity': 'Reviewer diversity',
  'settings.weightSessionContinuity': 'Session continuity',

  'settings.chief': 'Chief',
  'settings.chiefLede':
    'Whose subscription pays for Kimene’s own judgement. The board’s next-step options are produced on this account; with none designated, each session is analysed on its own — honest, but narrow, because the account doing the judging is the one being judged.',
  'settings.chiefAccount': 'Chief account',
  'settings.chiefAccountHint':
    'Choosing an account is the permission to spend it. A request goes out only when you press “propose next step” on the board. Its credential directory has to be reachable from the control host.',
  'settings.chiefAccountNone': 'None — each session is analysed on its own account',
  'settings.chiefAccountGone': 'The designated account no longer exists',
  'settings.chiefAccountOff': 'switched off',
  'settings.chiefModel': 'Model',
  'settings.chiefModelHint': 'Left empty, the CLI uses its own default.',
  'settings.chiefModelDefault': 'The CLI’s default',
  'settings.chiefPortfolio': 'Portfolio decisions',
  'settings.chiefPortfolioHint':
    'The chief reads every task in one turn and decides what should happen; “The chief’s decisions” on the board depends on this. The next-step options do not — designating the account is enough for those.',
  'settings.chiefPortfolioOn': 'On',
  'settings.chiefNeedsAccount': 'Choose a chief account before switching portfolio decisions on.',
  'settings.chiefLoopOn':
    'The chief decides on its own every {minutes} minutes. This screen has no field for that timer; to stop it, set autonomousEveryMinutes to zero on the record.',

  'settings.retentionLede': 'How long each class of history is kept, in days.',
  'settings.retentionEvents': 'Events',
  'settings.retentionEventsHint': 'The domain-event archive reconnecting clients replay from.',
  'settings.retentionRecordings': 'Session recordings',
  'settings.retentionRecordingsHint':
    'Raw terminal recordings — by far the largest class Kimene stores.',
  'settings.retentionAudit': 'Audit log',
  'settings.retentionAuditHint':
    '“Who did this, and were they allowed to?” — a question that can be asked years later.',
  'settings.retentionAuditNever': 'Never deleted',
  'settings.retentionKeepForever': 'Never expires',
  'settings.retentionStores': 'stored value: {value}',
  'settings.retentionInvalid': 'Enter a whole number of days.',
  'settings.days': 'days',
  'settings.notifyFailures': 'Failed tasks',
  'settings.themeDark': 'Dark',
  'settings.themeLight': 'Light',
  'settings.termFont': 'Terminal type size',
  'settings.termFontHint':
    "The pane's readability floor scales with it: larger type means a wider minimum for a tile.",
  'settings.termFontSmall': 'Small',
  'settings.termFontNormal': 'Normal',
  'settings.termFontLarge': 'Large',
  'settings.density': 'Density',
  'settings.densityTight': 'Tight',
  'settings.densityCompact': 'Normal',
  'settings.densityComfortable': 'Comfortable',
  'settings.densityHint':
    'Tight closes the frame gaps altogether: tiles meet on a single-line border, and tables gain a column rule.',
  'settings.appearanceLocal':
    'These preferences are stored in this browser only. They are not written to the server and will not follow you to another machine. Each applies as you choose it; there is nothing to save.',

  'settings.autonomyLede':
    'Whether Kimene may act unattended for each class of consequential action.',
  'settings.autonomyIsolatedWrites': 'Isolated writes',
  'settings.autonomyIsolatedWritesHint':
    'Writing only to the agent’s own working copy; no branch anybody else sees changes.',
  'settings.autonomyMainMerge': 'Merge to main',
  'settings.autonomyMainMergeHint': 'Moving the work onto the branch everyone else builds on.',
  'settings.autonomyProductionDeploy': 'Production deploy',
  'settings.autonomyProductionDeployHint': 'Putting a change live.',
  'settings.autonomyDestructiveAction': 'Destructive action',
  'settings.autonomyDestructiveActionHint':
    'Deletes, resets, force-writes — anything with no way back afterwards.',
  'settings.autonomyExternalPublish': 'External publish',
  'settings.autonomyExternalPublishHint':
    'Sending content outside Kimene. What leaves may have been copied even if it is later deleted.',
  'settings.autonomyFinancialSpend': 'Financial spend',
  'settings.autonomyFinancialSpendHint': 'Calls that cost money and resources that bill.',
  'settings.decisionAutonomous': 'Unattended',
  'settings.decisionRequireApproval': 'Asks first',
  'settings.decisionForbidden': 'Forbidden',
  'settings.decisionUnknown': 'unrecognised value',

  'settings.missingKey': 'The server holds no “{key}” setting.',
  'settings.missingKeyHint':
    'This key is written when the install is seeded. Until it exists the shipped default is in force; creating it here would present a value nobody chose as one somebody did.',
  'settings.updatedByAt': '{who} · {when}',
  'settings.noWeights': 'This setting holds no numeric weights.',
  'settings.noWeightsHint':
    'The stored value is not in the expected shape, so it cannot be edited here until it is corrected.',

  'settings.totpVerified': 'Verified in this session',
  'settings.totpNotVerified': 'Not verified in this session',
  'settings.totpHint':
    'A second factor applies to the whole account; it is not enabled per session.',
  'settings.totpEnrol': 'Set up a second factor',
  'settings.totpEnrolHint':
    'Add the secret to your authenticator app, then enter the code it produces. The factor is not enabled until a code has been accepted.',
  'settings.totpSecret': 'Secret',
  'settings.totpUrl': 'Setup URL',
  'settings.totpParams': '{digits} digits · {period} seconds',
  'settings.totpCode': 'Verification code',
  'settings.totpActivate': 'Verify and enable',
  'settings.totpRecovery': 'Recovery codes',
  'settings.totpRecoveryHint':
    'These are shown once and each works once. Store them somewhere safe now.',
  'settings.totpDisable': 'Remove second factor',
  'settings.totpDisableHint':
    'Removing it requires your password, so that a borrowed session cannot strip the control that was supposed to survive one.',

  // ---- The approval second factor (ADR-0032) ----
  'settings.approvalMfa': 'Second factor for approvals',
  'settings.approvalMfaLede':
    'Let only a session that presented its second factor decide a high-risk request.',
  'settings.approvalMfaRequire': 'L3 and L4 decisions',
  'settings.approvalMfaRequireHint':
    'Merges to main, production deploys, external publication, anything financial or security-touching. L1 and L2 are out of scope: gating those would spend an operator’s patience without moving the risk.',
  'settings.approvalMfaRequireOn': 'Require a second factor',
  'settings.approvalMfaNeedsEnrolment':
    'Enrol a second factor above first. This session presented none, so turning it on now would leave nobody able to decide a high-risk approval.',
  'settings.approvalMfaNeedsSignIn':
    'Your second factor is enrolled, but this session did not present it. Sign out and back in with a code; turning it on from a session without one would leave nobody able to decide a high-risk approval.',

  'settings.sessionExpires': 'Expires',
  'settings.sessionIdle': 'idle: {when}',
  'settings.sessionRevoked': 'Revoked',
  'settings.sessionRemembered': 'Kept open',

  'system.deployment': 'This deployment',
  'system.deploymentHint':
    'What the server reports about itself. None of it is editable here; it describes what is installed, not what is happening.',
  'system.buildVersion': 'Version',
  'system.buildCommit': 'Commit',
  'system.environment': 'Environment',
  'system.timezone': 'Server time zone',
  'system.serverTime': 'Server time',
  'system.capabilities': 'What this build can do',
  'system.cap.realtimeEvents': 'Live event stream',
  'system.cap.terminalGateway': 'Terminal gateway',
  'system.cap.idempotentWrite': 'Duplicate-safe writes',
  'system.cap.transactionalOutbox': 'Transactional outbox',
  'system.cap.webBundle': 'Embedded console',
  'system.cap.on': 'yes',
  'system.cap.off': 'no',
  'system.outboxIdempotent':
    'The datastore offers no transactions; the outbox republishes pending records instead. Delivery is at-least-once — consumers have to be duplicate-safe.',

  'settings.otherHint':
    'These keys have no dedicated editor. The server stores their values without validating them, so they are read-only here.',
  'settings.readOnly': 'read-only',
  'settings.serverText': 'The server’s own description',
  'settings.copyValue': 'Copy value',
  'settings.noOtherKeys': 'No other settings.',
  'settings.noOtherKeysHint': 'The server currently holds only the keys with editors above.',

  // ---- Artifacts ----
  'artifact.title': 'Artifacts',
  'artifact.subtitle': 'Documents, patches and reports the agents produced',
  'artifact.reviewState': 'Review state',
  'artifact.qualityScore': 'Quality score',
  'artifact.versionChain': 'Version chain',
  'artifact.contentHash': 'Content hash',
  'artifact.preview': 'Preview',
  'artifact.previewUnavailable': 'There is no text preview for this type.',
  'artifact.empty': 'No artifacts.',
  'artifact.emptyHint': 'As tasks complete, what they produce lands here.',
  'artifact.size': 'Size',
  'artifact.supersedes': 'Supersedes',
  'artifact.currentVersion': 'Current version',
  'artifact.review.PENDING': 'Pending',
  'artifact.review.APPROVED': 'Approved',
  'artifact.review.REJECTED': 'Rejected',
  'artifact.review.WAIVED': 'Waived',

  'artifact.scope': 'Scope',
  'artifact.chooseProject': 'Choose a project…',
  'artifact.projectsUnavailable': 'The project list could not be loaded.',
  'artifact.scopeRequired': 'Choose a project first.',
  'artifact.scopeRequiredHint':
    'The server will not list artifacts unscoped: this is the largest collection in the system, and an unfiltered dump of it is a page nobody reads.',
  'artifact.taskScope': 'This list is limited to task {taskId}.',
  'artifact.clearScope': 'Clear scope',
  'artifact.noProjects': 'There are no projects yet.',
  'artifact.noProjectsHint':
    'Artifacts are listed against a project or a task. Create a project on the Projects screen first.',

  'artifact.mission': 'Mission',
  'artifact.session': 'Session',
  'artifact.recordVersion': 'Record version',
  'artifact.provenance': 'Provenance',
  'artifact.provider': 'Provider',
  'artifact.account': 'Account profile',
  'artifact.model': 'Model',
  'artifact.agent': 'Agent instance',

  'artifact.chainUnavailable':
    'The server exposes only the immediately previous version; there is no endpoint that walks the whole chain.',
  'artifact.noPrevious': 'This is the first version.',
  'artifact.sources': 'Sources',
  'artifact.reviews': 'Reviews',
  'artifact.noReviews': 'This artifact has not been reviewed yet.',
  'artifact.reviewer': 'Reviewer',

  'artifact.download': 'Download the body',
  'artifact.previewNoBody': 'This record has no stored body.',
  'artifact.previewBinary': '{mime} is not shown as text. You can download the body instead.',
  'artifact.previewTooLarge':
    'Too large to preview ({size}). It is withheld rather than freezing the page — download the body.',
  'artifact.previewEmpty': 'The body is empty.',

  // ---- Tools ----
  'tool.title': 'Tool Registry',
  'tool.subtitle': 'Every tool an agent can call, and the roles that hold it',
  'tool.key': 'Key',
  'tool.destructive': 'Destructive',
  'tool.approvalRequired': 'Needs approval',
  'tool.roles': 'Roles holding it',
  'tool.noRoles': 'No role holds this tool.',
  'tool.rolesUnavailable': 'The role registry could not be read, so this column cannot be filled.',
  'tool.empty': 'No tools registered.',
  'tool.emptyHint': 'The tool registry is populated server-side.',

  // ---- Agents ----
  'agent.title': 'Agent Instances',
  'agent.subtitle': 'Role instances running right now',
  'agent.currentRoute': 'Current route',
  'agent.routeHistory': 'Route history',
  'agent.empty': 'No agent is running.',
  'agent.emptyHint': 'An agent instance appears when a task is routed.',
  'agent.startedAt': 'Started',
  'agent.currentTask': 'Current task',
  'agent.noHistory': 'No route change has happened.',
  'agent.routeHistoryIds':
    'Route history is served as identifiers only; no endpoint resolves one into an account, model and host.',
  'agent.noRouteDetail': 'The current route is known only by its identifier.',
  'agent.instanceName': 'Instance name',

  // ---- What the server does not provide ----
  //
  // These strings exist to say a gap out loud. A fabricated number, or a field
  // left quietly blank, tells an operator "measured, and it is zero"; this tells
  // them "not measured". That difference is what makes a decision right or wrong.
  'project.configInventoryUnavailable': 'No per-file inventory is served',
  'project.configInventoryHint':
    'The API returns no file listing for a project — only the configuration fingerprint and the last scan time. The five paths below are where Kimene looks; whether each is present cannot be read here.',
  'project.performanceUnavailable':
    'The API produces no per-model performance summary for a project. System-wide figures are on Analytics; the reasoning behind each route is on the Decisions tab.',
  'project.sessionsScopeNote':
    'The server does not filter sessions by project. This list is the open sessions that belong to this project.',
  'project.taskScopeNote':
    'The server has no per-project task listing; tasks are gathered mission by mission. Only the missions listed here contribute.',
  'common.truncated': 'The list was truncated; the count is a lower bound.',
  'task.evidenceRequirements': 'Evidence requirements',
  'task.evidenceNone': 'No extra evidence required',
  'task.notStarted': 'Not started',
  'mission.criticalPathComputed':
    'The critical path is computed in the browser from the dependencies; the server reports no such field.',
  'approval.noActions': 'No decision remains available on this request.',
  'approval.cancelRequest': 'Cancel the request',
  'approval.cancelRequestHint':
    'This closes the approval request. Cancelling the task itself is a separate action, taken from the task screen.',
  'approval.answerClarification': 'Answer the clarification',
  'approval.note': 'Note',

  // ---- Authentication ----
  'auth.secondFactor': 'Two-step verification',
  'auth.totpHint': 'Enter the six-digit code from your authenticator app.',
  'auth.useRecoveryCode': 'Use a recovery code',
  'auth.useAuthenticator': 'Back to the authenticator code',
  'auth.recoveryCode': 'Recovery code',
  'auth.recoveryCodeHint':
    'If you cannot reach your authenticator, enter one of your single-use recovery codes.',
  'auth.recoveryRequired': 'A recovery code is required.',
  'auth.recoveryInvalid': 'That recovery code is wrong, or has already been used.',
  'auth.rememberMe': 'Keep me signed in',
  'auth.rememberMeHint':
    'This browser stays signed in for much longer, even while you are away. Do not tick it on a shared computer.',
  'auth.signingIn': 'Signing in',
  'auth.usernameRequired': 'A username is required.',
  'auth.passwordRequired': 'A password is required.',
  'auth.totpRequired': 'This account requires a verification code.',
  'auth.totpIncomplete': 'Enter all six digits.',
  'auth.totpInvalid': 'That verification code is wrong.',
  'auth.serverNote':
    'Kimene runs on your own server; this form sends nothing to any outside service.',

  // ---- Setup wizard ----
  'setup.stepOf': 'Step {current} of {total}',
  'setup.finish': 'Finish setup',
  'setup.skip': 'Skip this step',
  'setup.dependenciesHint': 'These checks are live and refresh while this screen is open.',
  'setup.controlHostHint': 'The host Kimene itself runs on. Local execution happens here.',
  'setup.hostsHint': 'You do not have to add hosts now; you can add them later from Hosts.',
  'setup.nodeHint': 'The agent manages sessions on the host and reports metrics back.',
  'setup.accountsHint':
    'Provider sign-in happens on the host; credentials never reach the browser.',
  'setup.projectsHint': 'A project gathers one or more repositories and their quality gates.',
  'setup.routingHint': 'These defaults can be overridden per project.',
  'setup.orchestratorHint': 'Sets how much the orchestrator decides on its own.',
  'setup.finalCheckHint': 'Look everything over once more. The console opens after this.',
  'setup.passwordMismatch': 'The passwords do not match.',
  'setup.passwordTooShort': 'The password must be at least 12 characters.',
  'setup.confirmPassword': 'Confirm password',
  'setup.ownerCreated': 'The owner account was created.',
  'setup.stepFailed': 'This step could not be completed.',
  'setup.blockedByHealth': 'A required dependency is failing. Fix it before continuing.',
  'setup.dependencyDetailHidden':
    'Dependency detail is shown to signed-in users only; infrastructure topology is not disclosed to unauthenticated requests. The overall status is below — finish setup and sign in for the breakdown.',
  'cc.dependencyDetailHidden': 'Dependency detail is not available in this session.',
  'setup.nothingYet': 'Nothing here yet — this step can wait.',

  // ---- Not found ----
  'notfound.title': 'There is no such page',
  'notfound.hint': 'The address may be wrong, or the record may have been removed.',
  'notfound.home': 'Back to the Command Center',

  // ---- System risks ----
  //
  // Keyed by the server's risk `kind`, filled from its `params`. The server
  // also sends a composed English title and detail; those are the fallback for
  // a kind added after this table, so a new detector still says something.
  'risk.no_eligible_account': 'No account can accept work',
  'risk.no_eligible_account.detail':
    'Every profile is disabled, unconfigured, exhausted or awaiting authentication. Nothing will route until one recovers.',
  'risk.account_auth_required': 'Account {account} needs an interactive login',
  'risk.account_auth_required.detail':
    'Credentials are host-local; sign in from a terminal on {host}.',
  'risk.account_exhausted': 'Account {account} has hit a confirmed rate limit',
  'risk.host_offline': 'Host {host} is {status}',
  'risk.host_offline.detail': 'Work cannot be routed to it and any session on it is unreachable.',
  'risk.host_stale': 'Host {host} has not reported recently',
  'risk.host_stale.detail':
    'Its telemetry is older than {age}; treat its metrics as unknown rather than current.',
  'risk.approval_waiting': 'Approval waiting: {title}',
  'risk.approval_overdue': 'Approval overdue: {title}',
  'risk.approval_overdue.detail': 'It has been waiting a day for a decision. Nothing expires it; you decide.',
  'risk.dependency_down': '{dependency} is unreachable',
  'severity.critical': 'critical',
  'severity.error': 'error',
  'severity.warning': 'warning',
  'severity.info': 'info',
  // ---- Dashboard, last turn and the conductor's advice ----
  'nav.dashboard': 'Dashboard',
  'dashboard.title': 'Dashboard',
  'dashboard.count.calm': '{total} sessions',
  'dashboard.count.attention': '{total} sessions · {attention} waiting on you',
  'dashboard.search': 'Find a session',
  'dashboard.searchPlaceholder': 'name, account, model, host…',
  'dashboard.advice.title': 'Conductor\'s recommendations',
  'dashboard.advice.calm': 'Nothing to recommend',
  'dashboard.advice.open': 'Open',
  'dashboard.blind.title': 'The waiting signal',
  'dashboard.blind.body': 'the waiting signal is not working: not one of {count} agents has ever reported, so "waiting" cannot be measured',
  'dashboard.risks.title': 'Risks',
  'dashboard.posture.title': 'Pool status',
  'dashboard.posture.accounts': 'accounts can take work',
  'dashboard.posture.hosts': 'hosts online',
  'dashboard.posture.degraded': 'degraded',
  'dashboard.posture.day': 'in the last 24h',
  'dashboard.posture.completed': 'completed',
  'dashboard.posture.failed': 'failed',
  'dashboard.advice.openTask': 'Open task',
  'dashboard.sessions.empty': 'No sessions listed',
  'dashboard.sessions.emptyHint': 'Start a session; the dashboard follows it here.',
  'dashboard.sessions.noMatch': 'No session matches this filter',
  'dashboard.sessions.showAll': 'Show all',
  'dashboard.now.title': 'Now',
  'dashboard.fleet.title': 'Fleet',
  'dashboard.fleet.working': '{count} working',
  'dashboard.fleet.quiet': '{count} quiet',
  'dashboard.fleet.ended': '{count} ended',
  'dashboard.row.endedWhy': 'When the session ended.',
  'dashboard.silence.for': 'quiet {duration}',
  'dashboard.posture.unattended': 'sessions run without asking permission',
  'dashboard.posture.unattendedWhy':
    'These sessions were started with the provider CLI’s own permission prompts turned off (bypassApprovals): they do not stop to ask before writing or running a command — and their silence can never be “waiting for permission”.',
  'dashboard.silence.unmeasured': 'How long it has been quiet was never measured.',
  'dashboard.silence.from.done': 'The agent reported its turn was over; quiet since then.',
  'dashboard.silence.from.waiting': 'The agent reported it is waiting for a person; quiet since then.',
  'dashboard.silence.from.busy': 'It last produced output in its terminal this long ago.',
  'dashboard.silence.from.transcript':
    'Read from the agent’s own record — nothing holds a live terminal view of this session.',
  'session.lastTurn.title': 'Last turn',
  'session.lastTurn.prompt': 'Prompt',
  'session.lastTurn.reply': 'Reply',
  'session.lastTurn.tool': 'Last tool',
  'session.lastTurn.toolFailed': 'failed',
  'session.lastTurn.tools': '{calls} tool calls',
  'session.lastTurn.toolErrors': '{errors} errors',
  'session.lastTurn.files': '{count} files',
  'session.lastTurn.lastLine': 'Last line',
  'session.lastTurn.unmeasured': 'No record to read: the CLI keeps none, or the session has ended.',
  'session.lastTurn.turnOpen': 'the start of the turn is outside the window; counts are a floor',
  'session.lastTurn.atLeast': 'at least',
  'advice.answer_waiting_session': '{session} has waited for you for {minutes} minutes — answer it.',
  'advice.read_finished_turn': '{session} finished its turn {minutes} minutes ago and nobody has read it — read the result, give the next step.',
  'advice.switch_session_account': '{session} runs on {fromAccount} ({reason}) — at a safe boundary, switch it to {toAccount}.',
  'advice.session_account_blocked': '{session} runs on {fromAccount} ({reason}); no other account can take it — stop it, wait for the reset, or sign in.',
  'advice.restart_failed_session': '{session} failed — read the error, then start it again.',
  'advice.restart_failed_session.exit': '{session} failed (exit {exit}) — read the error, then start it again.',
  'advice.check_quiet_session': '{session} has been quiet for {minutes} minutes — see whether it is stuck or done; stop it if not needed.',
  'advice.tool_errors_in_turn': '{session} hit {errors} tool errors in its last turn — look at the commands.',
  'advice.idle_capacity': '{count} accounts are idle with capacity ({accounts}) — start work.',
  'advice.reason.exhausted': 'exhausted',
  'advice.reason.auth_required': 'needing a login',
  'advice.reason.disabled': 'disabled',

  // ---- Missions and tasks (M1) ----
  'work.title': 'Missions',
  'work.subtitle': 'A mission breaks an objective into tasks; every task completes on evidence',
  'work.count.calm': '{missions} missions · {tasks} open tasks',
  'work.count.attention': '{missions} missions · {tasks} open tasks · {attention} waiting for you',
  'work.search': 'Search tasks',
  'work.searchPlaceholder': 'title, objective, mission…',
  'work.band.all': 'All',
  'work.band.needsPerson': 'Waiting for you',
  'work.band.ready': 'Ready',
  'work.band.running': 'Running',
  'work.band.failed': 'Failed',
  'work.band.other': 'Other',
  'work.task.moved': 'Last moved',
  'work.task.noMatch': 'No task matches this filter',
  'work.task.showAll': 'Show all',
  'work.missions': 'Missions',
  'work.tasks': 'Tasks',
  'work.allOpenTasks': 'All open tasks',
  'work.createMission': 'Create mission',
  'work.createTask': 'Add task',
  'work.mission.title': 'Title',
  'work.mission.objective': 'Objective',
  'work.mission.project': 'Project',
  'work.mission.priority': 'Priority (0–100)',
  'work.mission.risk': 'Risk level',
  'work.mission.acceptance': 'Acceptance criteria (one per line)',
  'work.mission.counts': '{total} tasks · {running} running · {needsHuman} waiting for you',
  'work.mission.none': 'No missions yet',
  'work.mission.noneHint': 'A mission is the container that breaks an objective into tasks. Create the first.',
  'work.mission.select': 'Select a mission to see its tasks; with none selected, every open task is listed.',
  'work.mission.selected': 'Selected mission: {title}',
  'work.mission.clear': 'Clear selection',
  'work.mission.move': 'Change state',
  'work.mission.completeBlocked': 'A mission completes only from review and only once every task is resolved.',
  'work.task.title': 'Title',
  'work.task.objective': 'Objective',
  'work.task.archetype': 'Archetype',
  'work.task.priority': 'Priority (0–100)',
  'work.task.acceptance': 'Acceptance criteria (one per line)',
  'work.task.dependsOn': 'Depends on',
  'work.task.none': 'No tasks in this mission yet',
  'work.task.noneOpen': 'No open tasks',
  'work.task.noneHint': 'A task is one unit with one responsibility and explicit acceptance criteria. Add the first.',
  'work.task.state': 'State',
  'work.task.session': 'Session',
  'work.task.noSession': 'Not attached to a session yet',
  'work.task.sessionEnded': 'session ended',
  'work.task.attempts': '{count}/{max} attempts',
  'work.task.blocked': 'Blocked: {reason}',
  'work.task.evidence': 'Evidence contract',
  'work.task.evidenceHint': 'This task is not complete until its evidence contract is met; the contract can only be tightened.',
  'work.task.evidenceNone': 'No extra evidence required; a review is still needed.',
  'work.task.evidence.tests': 'tests pass',
  'work.task.evidence.build': 'build passes',
  'work.task.evidence.lint': 'lint passes',
  'work.task.evidence.diff': 'a real change',
  'work.task.evidence.reviewer': 'independent review',
  'work.task.evidence.sources': 'cited sources',
  'work.task.evidence.screenshot': 'a screenshot',
  'work.task.evidence.approval': 'human approval',
  'work.evidence.title': 'Collected evidence',
  'work.evidence.satisfied': 'Evidence clears the contract',
  'work.evidence.unsatisfied': 'Evidence does not clear the contract yet',
  'work.evidence.none': 'No evidence collected yet. The diff, build, test and lint are gathered once the task enters review.',
  'work.evidence.check.diff': 'change',
  'work.evidence.check.tests': 'tests',
  'work.evidence.check.build': 'build',
  'work.evidence.check.lint': 'lint',
  'work.evidence.check.forbiddenPaths': 'forbidden paths',
  'work.evidence.changedFiles': '{count} file(s)',
  'work.evidence.superseded': 'superseded',
  'work.route.title': 'Route',
  'work.route.preview': 'Why this account?',
  'work.route.none': 'No eligible route — the candidate accounts were rejected.',
  'work.route.reject.ACCOUNT_DISABLED': 'account disabled',
  'work.route.reject.ACCOUNT_CANNOT_ACCEPT_WORK': 'cannot accept work',
  'work.route.reject.ACCOUNT_NOT_REACHABLE_FROM_HOST': 'not reachable from this host',
  'work.route.reject.MODEL_UNAVAILABLE_FOR_ACCOUNT': 'model unavailable for the account',
  'work.route.reject.MODEL_BLOCKED': 'pairing blocked',
  'work.route.reject.HOST_OFFLINE': 'host offline',
  'work.route.reject.PROVIDER_CLI_NOT_INSTALLED': 'CLI not installed',
  'work.route.reject.REPOSITORY_NOT_AVAILABLE_ON_HOST': 'repo not on host',
  'work.route.reject.ACCOUNT_AT_CONCURRENCY_LIMIT': 'at concurrency limit',
  'work.route.reject.BLOCKED_BY_PROJECT_POLICY': 'blocked by project policy',
  'work.route.reject.PINNED_TO_A_DIFFERENT_ROUTE': 'pinned to a different route',
  'work.task.dependencies': '{count} dependencies',
  'work.task.open': 'Open task',
  'work.task.startSession': 'Start a session for this task',
  'work.task.nextStates': 'Possible next states',
  'work.task.actions': 'Decisions',
  'work.task.created': 'Task added',
  'work.decision.ready': 'Mark ready',
  'work.decision.review': 'Send to review',
  'work.decision.approve': 'Approve',
  'work.decision.rework': 'Request rework',
  'work.decision.cancel': 'Cancel',
  'work.decision.retry': 'Retry',
  'work.decision.pause': 'Pause',
  'work.decision.resume': 'Resume',
  'work.decision.reason': 'Reason (optional)',
  'work.decision.confirm': '{decision} — are you sure?',
  'work.startForTask': 'This session is started for the task "{title}"; the task advances to RUNNING and points at the session.',
  'work.missionState.DRAFT': 'Draft',
  'work.missionState.PLANNING': 'Planning',
  'work.missionState.ACTIVE': 'Active',
  'work.missionState.BLOCKED': 'Blocked',
  'work.missionState.PAUSED': 'Paused',
  'work.missionState.REVIEWING': 'Reviewing',
  'work.missionState.COMPLETED': 'Completed',
  'work.missionState.CANCELLED': 'Cancelled',
  'work.missionState.FAILED': 'Failed',
  'work.taskState.DRAFT': 'Draft',
  'work.taskState.ANALYZING': 'Analyzing',
  'work.taskState.PLANNED': 'Planned',
  'work.taskState.READY': 'Ready',
  'work.taskState.QUEUED': 'Queued',
  'work.taskState.ROUTING': 'Routing',
  'work.taskState.LEASED': 'Leased',
  'work.taskState.STARTING': 'Starting',
  'work.taskState.RUNNING': 'Running',
  'work.taskState.WAITING_FOR_AGENT': 'Waiting for agent',
  'work.taskState.WAITING_FOR_HUMAN': 'Waiting for a person',
  'work.taskState.PAUSED': 'Paused',
  'work.taskState.RATE_LIMITED': 'Rate limited',
  'work.taskState.AUTH_REQUIRED': 'Login required',
  'work.taskState.HOST_UNREACHABLE': 'Host unreachable',
  'work.taskState.HANDOFF_PENDING': 'Handoff pending',
  'work.taskState.POLICY_BLOCKED': 'Policy blocked',
  'work.taskState.FAILED_RETRYABLE': 'Failed (retryable)',
  'work.taskState.FAILED_FINAL': 'Failed',
  'work.taskState.REVIEWING': 'Reviewing',
  'work.taskState.REWORK_REQUIRED': 'Rework required',
  'work.taskState.APPROVAL_REQUIRED': 'Approval required',
  'work.taskState.COMPLETED': 'Completed',
  'work.taskState.CANCELLED': 'Cancelled',
  'work.archetype.planning': 'planning',
  'work.archetype.architecture': 'architecture',
  'work.archetype.frontend': 'frontend',
  'work.archetype.backend': 'backend',
  'work.archetype.mobile': 'mobile',
  'work.archetype.database': 'database',
  'work.archetype.testing': 'testing',
  'work.archetype.review': 'review',
  'work.archetype.research': 'research',
  'work.archetype.product': 'product',
  'work.archetype.design': 'design',
  'work.archetype.marketing': 'marketing',
  'work.archetype.advertising': 'advertising',
  'work.archetype.content': 'content',
  'work.archetype.devops': 'devops',
  'work.archetype.security': 'security',
  'work.archetype.analytics': 'analytics',
  'advice.decide_on_task': 'Task "{task}" is {state} — your decision is due (approve, request rework, or cancel).',
  'advice.start_ready_task': 'Task "{task}" is ready and nobody is working on it — start a session.',
  'advice.task_session_ended': 'Task "{task}" reads as running but its session has ended — send it to review or start another session.',

  // ---- The conductor (M2a) ----
  'conductor.title': 'Conductor',
  'conductor.run': 'Run the conductor',
  'conductor.lede': 'The conductor plans what to do and applies it when you approve; it starts nothing on its own.',
  'conductor.nothingToStart': 'No ready task to start right now.',
  'conductor.starts': 'To start ({count})',
  'conductor.reclaims': '{count} tasks would be returned to the queue (their session ended).',
  'conductor.deferred': '{count} tasks cannot start yet.',
  'conductor.approve': 'Approve and apply',
  'conductor.approveConfirm': '{count} sessions will be started and the conductor will send their prompts. Approve?',
  'conductor.applying': 'Applying: {count}/{total} —',
  'conductor.history': 'Past runs',
  'conductor.runSummary': '{starts} starts',

  // ---- Approvals (M6, ADR-0024) ----
  'approvals.card': 'This task is waiting for your decision',
  'approvals.rule.risk.l3-requires-approval': 'Irreversible work (L3)',
  'approvals.rule.risk.l4-requires-approval': 'Money or security (L4)',
  'approvals.rule.evidence.human-approval-required': 'Its contract asks for a person',
  'approvals.affects': 'Affects',
  'approvals.ifDenied': 'If denied',
  'approvals.requestedFor': 'on behalf of {actor}',
  'approvals.approve': 'Approve and complete',
  'approvals.reject': 'Reject, send back for rework',
  'approvals.note': 'Note (recorded)',
  'approvals.decidedBy': 'decided by {actor}',
  'approvals.mayNot':
    'You may not decide this. It needs: {permissions}. Whoever launched a change must not be the one who signs it off.',
  'approvals.state.PENDING': 'Waiting',
  'approvals.state.APPROVED': 'Approved',
  'approvals.state.REJECTED': 'Rejected',
  'approvals.state.CANCELLED': 'Cancelled',

  // ---- The chief's decisions, across the workforce (M5, ADR-0021) ----
  'supervisor.title': "The chief's decisions",
  'supervisor.decide': 'Ask the chief to decide',
  'supervisor.lede':
    'The chief reads the whole workforce and proposes what should happen next, with its reasons; nothing is applied until you approve it.',
  'supervisor.runsOn': 'The decision turn is spent on:',
  'supervisor.deciding': 'The chief is thinking — reading the whole workforce and deciding.',
  'supervisor.nothingToDo': 'The chief sees nothing to do right now. That is also a decision.',
  'supervisor.actions': 'Proposed steps ({count})',
  'supervisor.approve': 'Approve and apply',
  'supervisor.approveConfirm':
    '{count} steps will be applied. Any task that has moved since the decision is refused with a reason rather than applied. Approve?',
  'supervisor.history': 'Past decisions',
  'supervisor.noSummary': 'No summary',
  'supervisor.repairs': '{count} repair turns',
  'supervisor.kind.START_TASK': 'Start',
  'supervisor.kind.PAUSE_TASK': 'Pause',
  'supervisor.kind.RESUME_TASK': 'Resume',
  'supervisor.kind.SEND_TO_REVIEW': 'Send to review',
  'supervisor.kind.REQUEST_REWORK': 'Send back for rework',
  'supervisor.kind.REQUEST_REROUTE': 'Move to another account',
  'supervisor.kind.ESCALATE_TO_HUMAN': 'Hand to you',
  'supervisor.outcome.APPLIED': 'Applied',
  'supervisor.outcome.RECORDED': 'Recorded',
  'supervisor.outcome.REFUSED': 'Refused',
  'supervisor.outcome.FAILED': 'Failed',
  'supervisor.outcome.AWAITING_OPERATOR': 'Waiting for you',
  'supervisor.autonomous': 'on its own',
  'supervisor.loopOn': 'The chief decides every {minutes} minutes and carries out only the steps that stop work or hand it to you.',

  // ---- The chief, per session (ADR-0017) ----
  'chief.next': 'Next step',
  'chief.analyze': 'Propose next step',
  'chief.analyzeHint':
    'The chief reads the last turn, the project\'s repository and its branch, and offers the instructions that could be sent next (spends one request).',
  'chief.bymodel': 'from the model',
  'chief.nextPrompt': 'Prompt to send',
  'chief.nextPromptPlaceholder': 'The text to type into this session…',
  'chief.send': 'Send',
  'chief.sent': 'Sent',
  'chief.sendHint': '⌘/Ctrl + Enter to send · Esc to close',
  'chief.sendEmpty': 'There is nothing to send',
  'chief.finished': 'The chief reads this as finished — review it and stop the session if it is done.',
  'chief.unavailable': 'The model analysis could not run on this deployment; showing the deterministic proposal.',
  'chief.action.CONTINUE': 'Continue',
  'chief.action.ANSWER': 'Answer',
  'chief.action.FINISH': 'Finished',
  'chief.action.HOLD': 'Hold',

  'chief.reason.session_ended': 'The session has ended; there is nothing to send it.',
  'chief.reason.agent_working': 'The agent is working; wait for it to reach a boundary.',
  'chief.reason.agent_waiting': 'The agent is waiting for you ({reason}). Read the pane and answer it.',
  'chief.reason.agent_waiting_plain': 'The agent is waiting for you. Read the pane and answer it.',
  'chief.reason.tool_errors': 'The last turn had {errors} tool errors.',
  'chief.reason.turn_finished':
    'The agent finished a turn; propose a review-and-continue, or confirmation that it is done.',
  'chief.reason.no_signal':
    'The session is quiet with no clear signal; ask it to continue or confirm it is done.',
  'chief.reason.unstructured':
    'The analysis did not return a structured proposal; its text is offered as the next prompt.',
  'chief.note.account_unreachable':
    'The account is not reachable from the control host, so the model analysis could not run here.',
  'chief.note.no_transcript': 'There is no readable transcript to analyse.',
  'chief.note.analysis_failed': 'The model analysis could not be completed ({error}).',
  'chief.note.no_manager':
    'No manager account is designated, so the analysis ran on the session\'s own account. Choose one under Settings → Chief and every agent is read by the same judgement.',
  'chief.note.manager_unusable':
    'The designated manager account cannot be used right now, so these came from the rules. No other subscription was spent.',
  'chief.options': 'Options',
  'chief.runsOn': 'Chief',

  'hostfiles.title': 'Files',
  'hostfiles.subtitle': '{host} · {roots} registered roots',
  'hostfiles.local': 'Local',
  'hostfiles.remote': 'Remote',
  'hostfiles.colName': 'Name',
  'hostfiles.colSize': 'Size',
  'hostfiles.colModified': 'Modified',
  'hostfiles.chooseFolder': 'Choose a folder',
  'hostfiles.changeFolder': 'Change folder',
  'hostfiles.chooseFolderFirst': 'No folder chosen yet',
  'hostfiles.chooseFolderHint':
    'Choose a folder: the left pane really browses it, and what you download lands there rather than in the browser’s download folder.',
  'hostfiles.addFiles': 'Add files',
  'hostfiles.trayMode': 'This browser cannot open a local folder — what you pick collects here',
  'hostfiles.emptyLocal': 'This folder is empty',
  'hostfiles.emptyRemote': 'This directory is empty',
  'hostfiles.send': 'Send the selection to the server',
  'hostfiles.fetch': 'Bring the selection here',
  'hostfiles.localDenied': 'Permission to write to the folder was not granted.',
  'hostfiles.noRootsShort': 'No root selected',
  'hostfiles.discardConfirm': 'Throw the interrupted upload away? The bytes already sent are deleted.',
  'hostfiles.tabActive': 'Running',
  'hostfiles.tabFailed': 'Failed',
  'hostfiles.tabDone': 'Done',
  'hostfiles.clearFinished': 'Clear the finished',
  'hostfiles.queueEmpty': 'Nothing here.',
  'hostfiles.root': 'Root',
  'hostfiles.roots': 'Registered roots',
  'hostfiles.manageRoots': 'Manage roots',
  'hostfiles.noRoots': 'No registered roots',
  'hostfiles.noRootsHint':
    'Reaching a file needs somebody to have recorded which directory to look in. The registration is the boundary: a directory holding credential material is refused at that moment.',
  'hostfiles.addRoot': 'Add a root',
  'hostfiles.rootHost': 'Host',
  'hostfiles.rootPath': 'Absolute path',
  'hostfiles.rootPathHint': 'For example /opt/kimene, /var/log, /ssd/data/project/kimene',
  'hostfiles.rootLabel': 'Name',
  'hostfiles.rootWritable': 'Writable',
  'hostfiles.rootWritableHint':
    'Off means nothing can be uploaded here. Off by default: taking something off a machine is far more common than putting something on it.',
  'hostfiles.rootRemove': 'Withdraw',
  'hostfiles.refusedContains':
    'That directory holds {what}, so it cannot be registered. Choose a narrower one — the subdirectory the projects live in, for instance.',
  'hostfiles.refusedInside':
    'That directory is inside {what}. Narrowing will not help; the root has to be somewhere else.',
  'hostfiles.holds.account': 'provider account directories',
  'hostfiles.holds.keys': 'the hosts’ SSH keys',
  'hostfiles.holds.env': 'environment files carrying a password',
  'hostfiles.holds.ssh': 'SSH configuration',
  'hostfiles.holds.state': 'Kimene’s own state directory',
  'hostfiles.rootRemoveConfirm':
    'Withdraw this root? The audit records of what was taken from it stay readable.',
  'hostfiles.readOnlyRoot': 'read-only',
  'hostfiles.pathBar': 'Path',
  'hostfiles.pathBarHint': 'Paste an absolute path and press Enter; the root containing it is selected.',
  'hostfiles.pathNotInAnyRoot': 'That path is not inside any registered root.',
  'hostfiles.up': 'Up one',
  'hostfiles.truncated': 'This directory holds more than is shown.',
  'hostfiles.denied': 'Files with that name are not moved',
  'hostfiles.deniedHint':
    'Credential-shaped names are refused. This is a guardrail against clicking the wrong row, not a security boundary.',
  'hostfiles.outside': 'Leads outside the root',
  'hostfiles.incomplete': 'Interrupted upload',
  'hostfiles.incompleteHint':
    '{size} sent. Drop the same file again and it carries on from there, or throw it away.',
  'hostfiles.discard': 'Discard',
  'hostfiles.transfers': 'Transfers',
  'hostfiles.state.queued': 'Queued',
  'hostfiles.state.hashing': 'Hashing',
  'hostfiles.state.sending': 'Sending',
  'hostfiles.state.finishing': 'Verifying',
  'hostfiles.state.done': 'Done',
  'hostfiles.state.failed': 'Failed',
  'hostfiles.state.cancelled': 'Cancelled',
  'hostfiles.folderNeedsFolder':
    'Choose a destination folder on the left first. A folder cannot keep its shape in the browser’s downloads.',
  'hostfiles.treeTooBig':
    '“{name}” holds more files than one transfer will carry. Move the subfolders inside it separately.',
  'hostfiles.someSkipped':
    '{count} item(s) in the folder were not moved: they are either links leading out of the root or credential-shaped names.',
  'hostfiles.folderWorking':
    'Walking the folder and moving it — files appear in the queue below.',
  'hostfiles.resizePanes': 'Resize the panes',
  'hostfiles.resizePanesValue': 'Left pane {pct}%',
  'hostfiles.resizeQueue': 'Resize the transfer queue',
  'hostfiles.resizeQueueValue': '{px} pixels',
  'hostfiles.parallel': 'How many transfers at once',
  'hostfiles.parallelN': '{n} at once',
  'hostfiles.stop': 'Stop',
  'hostfiles.stopAll': 'Stop everything',
  'hostfiles.batchRunning': 'running',
  'hostfiles.batchQueued': 'queued',
  'hostfiles.queueMore': 'and {n} more files queued.',
  'hostfiles.newFolder': 'New folder',
  'hostfiles.newFolderName': 'Folder name',
  'hostfiles.rename': 'Rename',
  'hostfiles.renameTo': 'New name',
  'hostfiles.remove': 'Delete',
  'hostfiles.removeTitle': 'Delete from the server',
  'hostfiles.removeConfirm': '{files} files and {dirs} folders will be permanently deleted from the server. No wastebasket, no undo.',
  'hostfiles.removeTruncated': '“{name}” holds more than is shown, so what would be deleted is unknown and nothing will be. Delete the subfolders separately.',
  'hostfiles.removeTooMany': '“{name}” holds {count} items — more than this will delete in one go. A deletion that size belongs at a terminal.',
  'hostfiles.removeDenied':
    '“{item}” carries a credential-shaped name; nothing in the selection was deleted.',
  'hostfiles.removeRoot': 'The root itself cannot be deleted.',
  'hostfiles.opNotEmpty': 'The folder is not empty. Something in it may not be shown — an interrupted upload is not listed.',
  'hostfiles.opNameTaken': 'Something with that name is already there; it will not be replaced.',
  'hostfiles.opReadOnly':
    'That root is registered read-only; nothing is written to it. It may have changed since the listing was read.',
  'hostfiles.opChecksum':
    'What reached the host does not match what was sent; nothing was written. The partial file was discarded too, so a retry starts from the beginning.',
  'hostfiles.rootGone':
    'That root is not on its host. An unmounted disk or a moved directory looks like this.',
  'hostfiles.opNotAnEntry': 'That is not the kind of entry this screen moves.',
  'hostfiles.replaceTitle': 'Replace it?',
  'hostfiles.replaceAsk': 'A file named “{name}” is already on the server.',
  'hostfiles.replaceYes': 'Replace',
  'hostfiles.replaceSkip': 'Skip',
  'hostfiles.replaceAll': 'Do the same for the rest',
  'hostfiles.state.skipped': 'Skipped',
  'hostfiles.outsideHint': 'That name points outside the root; what it holds does not move from here.',
  'hostfiles.opNoRoot': 'That root is not on the server.',
  'hostfiles.downloadFailed': 'The file could not be fetched (the server answered {status}).',
  'hostfiles.leaveWarning': 'A transfer is still running.',

}
